import { gaziWinners, statistics, prizeGoldData } from './data.js';
import { translations, LEGEND_TITLES_EN, MONTHS_TR_TO_EN } from './translations.js';
import { parseTimeToSeconds, formatSecondsToTime, getJockeyFilename, getWeatherIcon } from './utils.js';
import weatherData from './weather_data.json';
import { logAnalyticsEvent } from './firebase.js';
import {
  isPollActive,
  isGazi2026Run,
  initPollActiveState,
  initPollModal,
  unsubscribePoll,
  renderPollUI
} from './modules/poll.js';

// Storage Constants & Helper Functions
const STORAGE_BUCKET_URL = "https://firebasestorage.googleapis.com/v0/b/gaziderby-a23d6.firebasestorage.app/o";
const getWinnerImageUrl = (year) => `${STORAGE_BUCKET_URL}/winners%2F${year}.webp?alt=media`;
const getJockeyImageUrl = (filename) => `${STORAGE_BUCKET_URL}/jockeys%2F${filename}?alt=media`;
const getPressImageUrl = (filename) => `${STORAGE_BUCKET_URL}/press_clippings%2F${filename}?alt=media`;

// State Variables
let activeLang = localStorage.getItem('gazi_lang') || 'tr';
let filteredWinners = [...gaziWinners].sort((a, b) => b.year - a.year); // Sorted newest first
let activeYear = (() => {
  const hash = window.location.hash;
  if (hash) {
    const yearMatch = hash.match(/^#(\d{4})(?:-.*)?$/);
    if (yearMatch) {
      const year = parseInt(yearMatch[1], 10);
      const exists = gaziWinners.some(w => w.year === year);
      if (exists) return year;
    }
  }
  return isGazi2026Run() ? 2026 : 2025;
})();

let hashInitialized = !!window.location.hash;
let currentSearchQuery = '';
let currentDecade = 'all';
let onlyLegends = false;
let currentCity = 'all';
let cardTransitionCounter = 0;
let isSimulatorDismissed = false;

// Stats state variables
let activeTab = 'timeline'; // 'timeline' or 'stats'
let chartPoints = []; // Stores point positions on canvas for hit testing
let hoverPoint = null;
let chartViewMode = 'time'; // 'time', 'speed' or 'prize'

// DOM Elements
const yearSliderTrack = document.getElementById('year-slider-track');
const sliderPrevBtn = document.getElementById('slider-prev-btn');
const sliderNextBtn = document.getElementById('slider-next-btn');

const championCard = document.getElementById('champion-card');
const championCardBg = document.getElementById('champion-card-bg');
const championCardContent = championCard.querySelector('.champion-card-content');
const championBadge = document.getElementById('champion-badge');
const championYear = document.getElementById('champion-year');
const championName = document.getElementById('champion-name');
const podiumBadges = document.getElementById('podium-badges');
const podiumBtnSecond = document.querySelector('.podium-btn.btn-second');
const podiumBtnThird = document.querySelector('.podium-btn.btn-third');
const podiumText = document.getElementById('podium-text');
const championTime = document.getElementById('champion-time');
const championJockey = document.getElementById('champion-jockey');
const championJockeyImg = document.getElementById('champion-jockey-img');
const championOwner = document.getElementById('champion-owner');
const championStory = document.getElementById('champion-story');
const storyLinkContainer = document.getElementById('story-link-container');
const championMoreLink = document.getElementById('champion-more-link');
const championVideoPreview = document.getElementById('champion-video-preview');
const previewThumbnail = document.getElementById('preview-thumbnail');

const championPressLink = document.getElementById('champion-press-link');
const storyLinkSeparator = document.getElementById('story-link-separator');
const pressModal = document.getElementById('press-modal');
const pressModalCloseBtn = document.getElementById('press-modal-close-btn');
const pressModalImage = document.getElementById('press-modal-image');
const pressModalTitle = document.getElementById('press-modal-title');
const pressModalSubtitle = document.getElementById('press-modal-subtitle');
const pressZoomBtn = document.getElementById('press-modal-zoom-btn');
const pressZoomText = document.getElementById('press-zoom-text');
const pressImageWrapper = document.querySelector('.press-image-wrapper');

const floatingInfoBtn = document.getElementById('floating-info-btn');
const aboutModal = document.getElementById('about-modal');
const aboutModalCloseBtn = document.getElementById('about-modal-close-btn');

const championVideoContainer = document.getElementById('champion-video-container');
const closeVideoBtn = document.getElementById('close-video-btn');
const playPauseBtn = document.getElementById('play-pause-btn');
const volumeBtn = document.getElementById('volume-btn');
const iconPlay = document.getElementById('icon-play');
const iconPause = document.getElementById('icon-pause');
const iconVolumeOn = document.getElementById('icon-volume-on');
const iconVolumeOff = document.getElementById('icon-volume-off');

const arenaPrevBtn = document.getElementById('arena-prev-btn');
const arenaNextBtn = document.getElementById('arena-next-btn');

// Sidebar / Filter Panel Elements
const sidebarPanel = document.getElementById('sidebar-panel');
const sidebarToggleBtn = document.getElementById('sidebar-toggle-btn');
const sidebarCloseBtn = document.getElementById('sidebar-close-btn');
const filterBadge = document.getElementById('filter-badge');

const searchInput = document.getElementById('search-input');
const decadeFilterContainer = document.getElementById('decade-filter-container');
const cityFilterContainer = document.getElementById('city-filter-container');
const legendToggle = document.getElementById('legend-toggle');

// Tab Elements
const navTimelineBtn = document.getElementById('nav-timeline-btn');
const navStatsBtn = document.getElementById('nav-stats-btn');
const mainStage = document.querySelector('.main-stage');
const statsStage = document.getElementById('stats-stage');
const logoBtn = document.getElementById('logo-btn');

// Stats Dashboard Elements
const statsTotalYears = document.getElementById('stats-total-years');
const timeTrendCanvas = document.getElementById('time-trend-canvas');
const chartTooltip = document.getElementById('chart-tooltip');
const jockeyBarChart = document.getElementById('jockey-bar-chart');
const ownerBarChart = document.getElementById('owner-bar-chart');

// Load YouTube Iframe Player API
const tag = document.createElement('script');
tag.src = "https://www.youtube.com/iframe_api";
const firstScriptTag = document.getElementsByTagName('script')[0];
firstScriptTag.parentNode.insertBefore(tag, firstScriptTag);

let ytPlayer = null;
let ytApiReady = false;

window.onYouTubeIframeAPIReady = () => {
  ytApiReady = true;
};

// Initial Setup
document.addEventListener('DOMContentLoaded', () => {
  const isAdminPath = window.location.pathname === '/admin' || window.location.pathname === '/admin/';
  if (isAdminPath) {
    document.body.classList.add('admin-mode');
    import('./admin.js').then(({ initAdmin }) => {
      initAdmin();
    });
    return;
  }

  if (floatingInfoBtn && sessionStorage.getItem('gazi_about_viewed') === 'true') {
    floatingInfoBtn.style.display = 'none';
  }

  setupIntroAnimation();
  initPollActiveState(gaziWinners, applyFilters);
  setupEventListeners();
  initLanguageSwitcher();
  switchLanguage(activeLang);
  renderStatistics();
  updateSidebarButtonState();
  setupSwipeGestures();
  updateSliderNavButtons();
  initGaziCountdown();
  initPollModal(() => activeLang, showToast);

  setTimeout(() => {
    updateSliderNavButtons();
    updateYearButtonScales();
  }, 150);
});

// Event Listeners
function setupEventListeners() {
  // Hash Change Event Listener to handle direct access and back/forward navigation
  window.addEventListener('hashchange', () => {
    hashInitialized = true;
    const hash = window.location.hash;
    if (!hash) {
      const defaultYear = isGazi2026Run() ? 2026 : 2025;
      if (activeYear !== defaultYear) {
        activeYear = defaultYear;
        if (activeTab !== 'timeline') {
          switchTab('timeline');
        }
        stopAndHideVideo();
        applyFilters();
      }
      return;
    }
    const yearMatch = hash.match(/^#(\d{4})$/);
    if (yearMatch) {
      const year = parseInt(yearMatch[1], 10);
      const exists = gaziWinners.some(w => w.year === year);
      if (exists) {
        if (year === 2026 && !isPollActive() && !isGazi2026Run()) {
          return;
        }
        if (activeYear !== year) {
          // Reset filters if the target year is not currently in filteredWinners
          const inFiltered = filteredWinners.some(w => w.year === year);
          if (!inFiltered) {
            currentSearchQuery = '';
            currentDecade = 'all';
            currentCity = 'all';
            onlyLegends = false;
            if (searchInput) searchInput.value = '';
            const searchActionBtn = document.getElementById('search-action-btn');
            if (searchActionBtn) {
              searchActionBtn.textContent = '🔍';
              searchActionBtn.title = 'Ara';
            }
            hideSuggestions();
            if (legendToggle) legendToggle.checked = false;
            if (decadeFilterContainer) {
              decadeFilterContainer.querySelectorAll('.btn-decade').forEach(btn => {
                btn.classList.remove('active');
                if (btn.getAttribute('data-decade') === 'all') btn.classList.add('active');
              });
            }
            if (cityFilterContainer) {
              cityFilterContainer.querySelectorAll('.btn-decade').forEach(btn => {
                btn.classList.remove('active');
                if (btn.getAttribute('data-city') === 'all') btn.classList.add('active');
              });
            }
          }
          
          activeYear = year;
          if (activeTab !== 'timeline') {
            switchTab('timeline');
          }
          stopAndHideVideo();
          applyFilters();
        }
      }
    }
  });

  // Brand Logo Click (Reset to Initial State)
  if (logoBtn) {
    logoBtn.addEventListener('click', (e) => {
      e.preventDefault();
      resetToInitialState();
    });
  }

  // Search Autocomplete & Clear Action Button
  initSearchAutocomplete();

  // Decade Buttons
  decadeFilterContainer.addEventListener('click', (e) => {
    if (e.target.classList.contains('btn-decade')) {
      decadeFilterContainer.querySelectorAll('.btn-decade').forEach(btn => {
        btn.classList.remove('active');
      });
      e.target.classList.add('active');

      currentDecade = e.target.getAttribute('data-decade');
      applyFilters();
    }
  });

  // City Buttons
  if (cityFilterContainer) {
    cityFilterContainer.addEventListener('click', (e) => {
      if (e.target.classList.contains('btn-decade')) {
        cityFilterContainer.querySelectorAll('.btn-decade').forEach(btn => {
          btn.classList.remove('active');
        });
        e.target.classList.add('active');

        currentCity = e.target.getAttribute('data-city');
        applyFilters();
      }
    });
  }

  // Legends Toggle
  legendToggle.addEventListener('change', (e) => {
    onlyLegends = e.target.checked;
    applyFilters();
  });

  // Sidebar Open/Close Drawer (Mobile) & Collapse (Desktop)
  sidebarToggleBtn.addEventListener('click', () => {
    if (window.innerWidth <= 992) {
      sidebarPanel.classList.toggle('active');
    } else {
      sidebarPanel.classList.toggle('collapsed');
    }
    updateSidebarButtonState();
  });

  sidebarCloseBtn.addEventListener('click', () => {
    sidebarPanel.classList.remove('active');
    updateSidebarButtonState();
  });

  // Arena Navigation
  arenaPrevBtn.addEventListener('click', () => {
    navigateYear(-1);
    showSwipeHint();
  });
  arenaNextBtn.addEventListener('click', () => {
    navigateYear(1);
    showSwipeHint();
  });

  // Slider Track Navigation
  sliderPrevBtn.addEventListener('click', () => {
    yearSliderTrack.scrollBy({ left: -200, behavior: 'smooth' });
  });
  sliderNextBtn.addEventListener('click', () => {
    yearSliderTrack.scrollBy({ left: 200, behavior: 'smooth' });
  });
  yearSliderTrack.addEventListener('scroll', () => {
    updateSliderNavButtons();
    updateYearButtonScales();
  });

  // Keyboard Navigation
  document.addEventListener('keydown', (e) => {
    if (e.key === 'ArrowLeft') {
      navigateYear(-1); // Move left (newer/leftmost button, index decreases)
    } else if (e.key === 'ArrowRight') {
      navigateYear(1); // Move right (older/rightmost button, index increases)
    }
  });

  // Watch Video Inline Player Trigger via Thumbnail click
  championVideoPreview.addEventListener('click', () => {
    const videoId = championVideoPreview.getAttribute('data-video-id');
    if (videoId) {
      playVideoInline(videoId);
    }
  });

  // Play / Pause Control Button
  playPauseBtn.addEventListener('click', () => {
    if (ytPlayer && typeof ytPlayer.getPlayerState === 'function') {
      const state = ytPlayer.getPlayerState();
      if (state === 1) { // 1 = YT.PlayerState.PLAYING
        ytPlayer.pauseVideo();
      } else {
        ytPlayer.playVideo();
      }
    }
  });

  // Volume Mute Control Button
  volumeBtn.addEventListener('click', () => {
    if (ytPlayer && typeof ytPlayer.isMuted === 'function') {
      if (ytPlayer.isMuted()) {
        ytPlayer.unMute();
        toggleVolumeIcons(false);
      } else {
        ytPlayer.mute();
        toggleVolumeIcons(true);
      }
    }
  });

  // Close Video Button overlay
  closeVideoBtn.addEventListener('click', stopAndHideVideo);

  // Toggle Champion Story Box (Accordion) & parent card height (Only on mobile/tablet)
  const storyToggleBox = document.getElementById('story-toggle-box');
  if (storyToggleBox) {
    storyToggleBox.addEventListener('click', () => {
      // Desktop görünümünde açılıp kapanma eylemini engelle (hep açık kalsın) - Kısa ekranlar hariç
      if (window.innerWidth > 992 && window.innerHeight > 850) return;
      
      storyToggleBox.classList.toggle('active');
      if (championCard) {
        championCard.classList.toggle('story-active');
      }
    });
  }

  // Navigation Tabs Event Listeners
  if (navTimelineBtn && navStatsBtn) {
    navTimelineBtn.addEventListener('click', () => switchTab('timeline'));
    navStatsBtn.addEventListener('click', () => switchTab('stats'));
  }

  // Mobile Bottom Navigation Tabs Event Listeners
  const mobileNavTimelineBtn = document.getElementById('mobile-nav-timeline-btn');
  const mobileNavStatsBtn = document.getElementById('mobile-nav-stats-btn');
  if (mobileNavTimelineBtn && mobileNavStatsBtn) {
    mobileNavTimelineBtn.addEventListener('click', () => switchTab('timeline'));
    mobileNavStatsBtn.addEventListener('click', () => switchTab('stats'));
  }

  // Canvas Chart Event Listeners
  if (timeTrendCanvas) {
    timeTrendCanvas.addEventListener('mousemove', handleChartMouseMove);
    timeTrendCanvas.addEventListener('mouseleave', handleChartMouseLeave);
  }

  // Jockey Bar Chart Click Event Listener (Search & Switch to Timeline)
  if (jockeyBarChart) {
    jockeyBarChart.addEventListener('click', (e) => {
      const item = e.target.closest('.clickable-jockey');
      if (item) {
        const jockeyName = item.getAttribute('data-jockey');
        if (jockeyName) {
          currentSearchQuery = jockeyName.toLowerCase().trim();
          if (searchInput) searchInput.value = jockeyName;
          applyFilters();
          switchTab('timeline');
          const toastMsg = activeLang === 'en'
            ? `Timeline filtered for "${jockeyName}".`
            : `"${jockeyName}" için zaman tüneli filtrelendi.`;
          showToast(toastMsg);
        }
      }
    });
  }

  // Owner Bar Chart Click Event Listener (Search & Switch to Timeline)
  if (ownerBarChart) {
    ownerBarChart.addEventListener('click', (e) => {
      const item = e.target.closest('.clickable-owner');
      if (item) {
        const ownerName = item.getAttribute('data-owner');
        if (ownerName) {
          const displayOwner = ownerName.replace('Ekürisi', activeLang === 'en' ? 'Stable' : 'Ekürisi');
          currentSearchQuery = displayOwner.toLowerCase().trim();
          if (searchInput) searchInput.value = displayOwner;
          applyFilters();
          switchTab('timeline');
          const toastMsg = activeLang === 'en'
            ? `Timeline filtered for "${displayOwner}".`
            : `"${displayOwner}" için zaman tüneli filtrelendi.`;
          showToast(toastMsg);
        }
      }
    });
  }

  // Champion Card Jockey Click Event Listener (Silent Search)
  const jockeyContainer = document.querySelector('.jockey-profile-container');
  if (jockeyContainer) {
    jockeyContainer.addEventListener('click', () => {
      const jockeyName = championJockey ? championJockey.textContent.trim() : '';
      if (jockeyName && jockeyName !== '-' && jockeyName !== 'Kayıt Yok') {
        currentSearchQuery = jockeyName.toLowerCase().trim();
        if (searchInput) searchInput.value = jockeyName;
        applyFilters();
      }
    });
  }

  // Analysis Modal Open/Close
  const btnReadAnalysis = document.getElementById('btn-read-analysis');
  const analysisModal = document.getElementById('analysis-modal');
  const analysisModalCloseBtn = document.getElementById('analysis-modal-close-btn');

  if (btnReadAnalysis && analysisModal) {
    btnReadAnalysis.addEventListener('click', () => {
      analysisModal.style.display = 'flex';
      document.body.style.overflow = 'hidden';
    });
  }

  if (analysisModalCloseBtn && analysisModal) {
    analysisModalCloseBtn.addEventListener('click', () => {
      analysisModal.style.display = 'none';
      document.body.style.overflow = '';
    });
    analysisModal.addEventListener('click', (e) => {
      if (e.target === analysisModal) {
        analysisModal.style.display = 'none';
        document.body.style.overflow = '';
      }
    });
  }

  // Resize window synchronization
  window.addEventListener('resize', () => {
    updateSidebarButtonState();
    updateSliderNavButtons();
    if (activeTab === 'stats') {
      renderStatsDashboard();
    }
  });

  // Share Card Action
  const btnShare = document.getElementById('btn-share-card');
  if (btnShare) {
    btnShare.addEventListener('click', () => {
      const winner = filteredWinners.find(w => w.year === activeYear);
      if (winner) generateAndShareCard(winner);
    });
  }

  // Toggle Stats Trivia/Records section
  const btnStatsTriviaToggle = document.getElementById('btn-stats-trivia-toggle');
  const statsTriviaContainer = document.querySelector('.stats-trivia-container');
  if (btnStatsTriviaToggle && statsTriviaContainer) {
    btnStatsTriviaToggle.addEventListener('click', () => {
      const isActive = statsTriviaContainer.classList.toggle('active');
      btnStatsTriviaToggle.classList.toggle('active', isActive);
      
      if (isActive) {
        setTimeout(() => {
          statsTriviaContainer.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
        }, 100);
      }
    });
  }

  // Chart View Toggle Switcher
  const chartToggleButtons = document.querySelectorAll('.btn-chart-toggle');
  chartToggleButtons.forEach(btn => {
    btn.addEventListener('click', (e) => {
      chartToggleButtons.forEach(b => b.classList.remove('active'));
      e.target.classList.add('active');
      chartViewMode = e.target.getAttribute('data-view');
      drawTimeTrendChart();
    });
  });

  // Lazy load Quiz module
  const sidebarStartBtn = document.getElementById('sidebar-quiz-start-btn');
  if (sidebarStartBtn) {
    sidebarStartBtn.addEventListener('click', () => {
      if (!window.quizInitialized) {
        import('./modules/quiz.js').then(({ initQuiz }) => {
          initQuiz(() => activeLang, showToast);
          window.quizInitialized = true;
          sidebarStartBtn.click(); // Re-trigger click so it executes the newly bound listener in initQuiz
        });
      }
    });
  }

  // Lazy load Simulator module
  const btnSimulateRace = document.getElementById('btn-simulate-race');
  if (btnSimulateRace) {
    btnSimulateRace.addEventListener('click', () => {
      if (!window.simulatorInitialized) {
        import('./modules/simulator.js').then(({ initRaceSimulator }) => {
          initRaceSimulator(() => activeLang, () => gaziWinners, isGazi2026Run);
          window.simulatorInitialized = true;
          btnSimulateRace.click(); // Re-trigger click so it executes the newly bound listener in initRaceSimulator
        });
      }
    });
  }

  // Simulator close button listener
  const btnSimulateClose = document.getElementById('btn-simulate-close');
  if (btnSimulateClose) {
    btnSimulateClose.addEventListener('click', (e) => {
      e.stopPropagation();
      isSimulatorDismissed = true;
      const simulateRaceContainer = document.getElementById('simulate-race-container');
      if (simulateRaceContainer) {
        simulateRaceContainer.style.display = 'none';
      }
    });
  }

  // Interactive Podium Badges/Buttons (2nd and 3rd Place)
  const podiumBtns = document.querySelectorAll('.podium-btn');
  podiumBtns.forEach(btn => {
    btn.addEventListener('click', (e) => {
      e.stopPropagation();
      const place = btn.getAttribute('data-place'); // "2" or "3"
      const isCurrentlyActive = btn.classList.contains('active');

      // Find current winner object
      const currentWinner = filteredWinners.find(w => w.year === activeYear);
      if (!currentWinner) return;

      // Reset all buttons first
      podiumBtns.forEach(b => b.classList.remove('active'));
      if (podiumText) podiumText.classList.remove('show');

      if (!isCurrentlyActive) {
        btn.classList.add('active');
        const horseName = place === '2' ? currentWinner.secondPlace : currentWinner.thirdPlace;
        const medal = place === '2' ? '🥈 ' : '🥉 ';
        
        if (podiumText) {
          podiumText.textContent = `${medal}${horseName}`;
          podiumText.classList.add('show');
        }
      } else {
        // If clicked active one, it toggles off
        if (podiumText) podiumText.textContent = '';
      }
    });
  });

  // Press Clipping Modal opening & zooming
  let isZoomed = false;

  const togglePressZoom = () => {
    isZoomed = !isZoomed;
    if (isZoomed) {
      pressModalImage.style.transform = 'scale(1.8)';
      pressModalImage.style.cursor = 'zoom-out';
      if (pressZoomText) {
        pressZoomText.textContent = activeLang === 'en' ? 'Zoom Out' : 'Küçült';
      }
      if (pressImageWrapper) {
        pressImageWrapper.style.cursor = 'move';
      }
    } else {
      pressModalImage.style.transform = 'scale(1)';
      pressModalImage.style.cursor = 'zoom-in';
      if (pressZoomText) {
        pressZoomText.textContent = activeLang === 'en' ? 'Zoom In' : 'Büyüt';
      }
      if (pressImageWrapper) {
        pressImageWrapper.style.cursor = 'grab';
      }
    }
  };

  if (championPressLink && pressModal) {
    championPressLink.addEventListener('click', (e) => {
      e.preventDefault();
      const clipFile = championPressLink.getAttribute('data-clip');
      const year = championPressLink.getAttribute('data-year');
      const horse = championPressLink.getAttribute('data-horse');

      if (clipFile) {
        pressModalImage.src = getPressImageUrl(clipFile);
        
        // Update title/subtitle based on language
        const dateStr = clipFile.replace('.jpg', ''); // e.g. "10.06.1972"
        const parts = dateStr.split('.');
        let subtitleHtml = '';

        if (parts.length === 3) {
          const day = parts[0];
          const month = parts[1];
          const yearVal = parts[2];
          const queryDate = `${yearVal}.${month}.${day}`; // "1972.06.10"
          const sourceUrl = `https://gazetearsivi.milliyet.com.tr/liste?tarih=${queryDate}`;

          if (activeLang === 'en') {
            pressModalTitle.textContent = `Historical Newspaper Clipping`;
            subtitleHtml = `Milliyet newspaper clipping dated ${dateStr} (<a href="${sourceUrl}" target="_blank" rel="noopener noreferrer" style="color: var(--color-gold-light); text-decoration: underline;">Source</a>)`;
          } else {
            pressModalTitle.textContent = `Tarihi Gazete Kupürü`;
            subtitleHtml = `${dateStr} tarihli Milliyet gazetesi kupürü (<a href="${sourceUrl}" target="_blank" rel="noopener noreferrer" style="color: var(--color-gold-light); text-decoration: underline;">Kaynak</a>)`;
          }
        } else {
          if (activeLang === 'en') {
            pressModalTitle.textContent = `Historical Newspaper Clipping`;
            subtitleHtml = `Published on the day after the ${year} Gazi Derby (Winner: ${horse})`;
          } else {
            pressModalTitle.textContent = `Tarihi Gazete Kupürü`;
            subtitleHtml = `${year} yılı Gazi Koşusu'nun ertesi gününe ait basın küpürü (Şampiyon: ${horse})`;
          }
        }
        pressModalSubtitle.innerHTML = subtitleHtml;

        // Reset zoom state on open
        isZoomed = false;
        pressModalImage.style.transform = 'scale(1)';
        pressModalImage.style.cursor = 'zoom-in';
        if (pressZoomText) {
          pressZoomText.textContent = activeLang === 'en' ? 'Zoom In' : 'Büyüt';
        }
        if (pressImageWrapper) {
          pressImageWrapper.style.cursor = 'grab';
        }

        pressModal.style.display = 'flex';
      }
    });
  }

  // Close press modal
  if (pressModalCloseBtn && pressModal) {
    pressModalCloseBtn.addEventListener('click', () => {
      pressModal.style.display = 'none';
      pressModalImage.src = '';
    });

    // Close on overlay click
    pressModal.addEventListener('click', (e) => {
      if (e.target === pressModal) {
        pressModal.style.display = 'none';
        pressModalImage.src = '';
      }
    });
  }

  if (pressZoomBtn) {
    pressZoomBtn.addEventListener('click', togglePressZoom);
  }

  if (pressModalImage) {
    pressModalImage.addEventListener('click', (e) => {
      e.stopPropagation(); // Avoid closing the modal when clicking on the image itself
      togglePressZoom();
    });
  }

  // About Modal opening & closing
  if (floatingInfoBtn && aboutModal) {
    floatingInfoBtn.addEventListener('click', (e) => {
      e.preventDefault();
      aboutModal.style.display = 'flex';
      sessionStorage.setItem('gazi_about_viewed', 'true');
      floatingInfoBtn.style.display = 'none';
    });
  }

  if (aboutModalCloseBtn && aboutModal) {
    aboutModalCloseBtn.addEventListener('click', () => {
      aboutModal.style.display = 'none';
    });

    aboutModal.addEventListener('click', (e) => {
      if (e.target === aboutModal) {
        aboutModal.style.display = 'none';
      }
    });
  }
}

// Filter Logic
function applyFilters() {
  filteredWinners = gaziWinners.filter(winner => {
    // Exclude 2026 if poll is not active and not run
    if (winner.year === 2026 && !isPollActive() && !isGazi2026Run()) {
      return false;
    }

    // Search match
    const searchMatch = 
      winner.horseName.toLowerCase().includes(currentSearchQuery) ||
      winner.jockey.toLowerCase().includes(currentSearchQuery) ||
      (winner.owner && (
        winner.owner.toLowerCase().includes(currentSearchQuery) ||
        normalizeOwnerName(winner.owner).toLowerCase().includes(currentSearchQuery) ||
        normalizeOwnerName(winner.owner).replace('Ekürisi', 'Stable').toLowerCase().includes(currentSearchQuery)
      )) ||
      winner.year.toString().includes(currentSearchQuery) ||
      (winner.city && winner.city.toLowerCase().includes(currentSearchQuery)) ||
      (winner.city === "İstanbul" && "istanbul".includes(currentSearchQuery));

    // Decade match
    let decadeMatch = true;
    if (currentDecade !== 'all') {
      const [startYear, endYear] = currentDecade.split('-').map(Number);
      decadeMatch = winner.year >= startYear && winner.year <= endYear;
    }

    // City match
    let cityMatch = true;
    if (currentCity !== 'all') {
      cityMatch = winner.city === currentCity;
    }

    // Legend match
    const legendMatch = !onlyLegends || winner.isLegend;

    return searchMatch && decadeMatch && cityMatch && legendMatch;
  });

  // Sort newest first
  filteredWinners.sort((a, b) => b.year - a.year);

  // Update Filter Badge Count
  updateFilterBadge();

  // Render Year Selector Slider
  renderYearSlider();

  // Determine active year
  if (filteredWinners.length > 0) {
    // If we haven't initialized from hash and 2026 has been run, default to 2026
    if (!hashInitialized && isGazi2026Run() && activeYear === 2025) {
      activeYear = 2026;
    }
    // If the active year is not in the filtered list, set it to the first filtered item
    const stillExists = filteredWinners.some(w => w.year === activeYear);
    if (!stillExists) {
      activeYear = filteredWinners[0].year;
    }
    const currentWinner = filteredWinners.find(w => w.year === activeYear);
    updateChampionCard(currentWinner);
  } else {
    showNoResultsCard();
  }

  // Update the stats dashboard (stat cards, bar charts, canvas drawing)
  renderStatsDashboard();
}

// Render Year Slider Buttons
function renderYearSlider() {
  let html = filteredWinners.map(winner => {
    const isActive = winner.year === activeYear ? 'active' : '';
    const isLegend = winner.isLegend ? 'legend-year' : '';
    
    let indicators = '';
    if (winner.year === 1938) {
      const infTitle = activeLang === 'en' ? "In Memory of Atatürk (∞)" : "Atatürk'ün Anısına (∞)";
      indicators += `<span class="indicator-infinity" title="${infTitle}">∞</span>`;
    }
    if (winner.isLegend) {
      indicators += '<span class="indicator-dot legend-dot" title="Efsane Koşu"></span>';
    }
    if (winner.year === 1996) {
      indicators += '<span class="indicator-dot game-dot" title="Yarıştır Oyunu"></span>';
    }

    return `
      <button class="btn-year ${isActive} ${isLegend}" data-year="${winner.year}">
        <span class="year-text">${winner.year}</span>
        ${indicators ? `<div class="year-indicators">${indicators}</div>` : ''}
      </button>
    `;
  }).join('');

  // Append active filters at the end
  const hasSearch = currentSearchQuery !== '';
  const hasDecade = currentDecade !== 'all';
  const hasCity = currentCity !== 'all';
  const hasLegend = onlyLegends === true;

  if (hasSearch || hasDecade || hasCity || hasLegend) {
    let filterBadges = '';
    if (hasSearch) {
      const displayQuery = currentSearchQuery.length > 12 ? currentSearchQuery.substring(0, 10) + '...' : currentSearchQuery;
      const titleSearch = activeLang === 'en' ? 'Clear Search' : 'Aramayı Temizle';
      filterBadges += `
        <div class="active-filter-badge" data-filter-type="search">
          <span class="badge-label">"${displayQuery}"</span>
          <button class="btn-clear-single-filter" data-filter-type="search" title="${titleSearch}">✕</button>
        </div>
      `;
    }
    if (hasDecade) {
      const titleDecade = activeLang === 'en' ? 'Clear Decade' : 'Dönemi Temizle';
      filterBadges += `
        <div class="active-filter-badge" data-filter-type="decade">
          <span class="badge-label">${currentDecade.replace('-', ' - ')}</span>
          <button class="btn-clear-single-filter" data-filter-type="decade" title="${titleDecade}">✕</button>
        </div>
      `;
    }
    if (hasCity) {
      const cityLabel = activeLang === 'en' ? (currentCity === 'İstanbul' ? 'Istanbul' : 'Ankara') : currentCity;
      const titleCity = activeLang === 'en' ? 'Clear City' : 'Şehri Temizle';
      filterBadges += `
        <div class="active-filter-badge" data-filter-type="city">
          <span class="badge-label">${cityLabel}</span>
          <button class="btn-clear-single-filter" data-filter-type="city" title="${titleCity}">✕</button>
        </div>
      `;
    }
    if (hasLegend) {
      const legendLabel = activeLang === 'en' ? 'Legend' : 'Efsane';
      const titleLegends = activeLang === 'en' ? 'Clear Legends' : 'Efsaneleri Temizle';
      filterBadges += `
        <div class="active-filter-badge" data-filter-type="legend">
          <span class="badge-label">${legendLabel}</span>
          <button class="btn-clear-single-filter" data-filter-type="legend" title="${titleLegends}">✕</button>
        </div>
      `;
    }
    html += filterBadges;
  }

  yearSliderTrack.innerHTML = html;

  // Add click handlers to slider buttons
  const yearButtons = yearSliderTrack.querySelectorAll('.btn-year');
  yearButtons.forEach(btn => {
    btn.addEventListener('click', () => {
      hashInitialized = true;
      const year = parseInt(btn.getAttribute('data-year'));
      activeYear = year;
      
      stopAndHideVideo();
      
      // Update active state in slider UI
      yearButtons.forEach(b => b.classList.remove('active'));
      btn.classList.add('active');
      
      const winner = filteredWinners.find(w => w.year === activeYear);
      updateChampionCard(winner);
      centerActiveYearButton();
    });
  });

  // Add click handlers to clear filter buttons
  const clearFilterBtns = yearSliderTrack.querySelectorAll('.btn-clear-single-filter');
  clearFilterBtns.forEach(btn => {
    btn.addEventListener('click', (e) => {
      e.stopPropagation();
      const filterType = btn.getAttribute('data-filter-type');
      clearSingleFilter(filterType);
    });
  });

  centerActiveYearButton();
  updateYearButtonScales();
}

// Clear Individual Filter
function clearSingleFilter(filterType) {
  if (filterType === 'search') {
    currentSearchQuery = '';
    if (searchInput) {
      searchInput.value = '';
    }
    const searchActionBtn = document.getElementById('search-action-btn');
    if (searchActionBtn) {
      searchActionBtn.textContent = '🔍';
      searchActionBtn.title = 'Ara';
    }
    hideSuggestions();
  } else if (filterType === 'decade') {
    currentDecade = 'all';
    if (decadeFilterContainer) {
      decadeFilterContainer.querySelectorAll('.btn-decade').forEach(btn => {
        btn.classList.remove('active');
        if (btn.getAttribute('data-decade') === 'all') {
          btn.classList.add('active');
        }
      });
    }
  } else if (filterType === 'city') {
    currentCity = 'all';
    if (cityFilterContainer) {
      cityFilterContainer.querySelectorAll('.btn-decade').forEach(btn => {
        btn.classList.remove('active');
        if (btn.getAttribute('data-city') === 'all') {
          btn.classList.add('active');
        }
      });
    }
  } else if (filterType === 'legend') {
    onlyLegends = false;
    if (legendToggle) {
      legendToggle.checked = false;
    }
  }
  
  applyFilters();
}

// Center active year button in track slider
function centerActiveYearButton() {
  setTimeout(() => {
    const activeButton = yearSliderTrack.querySelector('.btn-year.active');
    if (activeButton) {
      const trackRect = yearSliderTrack.getBoundingClientRect();
      const buttonRect = activeButton.getBoundingClientRect();
      // Calculate relative position within the scroll area to avoid page horizontal shift
      const buttonRelativeLeft = buttonRect.left - trackRect.left + yearSliderTrack.scrollLeft;
      const targetScrollLeft = buttonRelativeLeft - (trackRect.width / 2) + (buttonRect.width / 2);
      
      yearSliderTrack.scrollTo({
        left: targetScrollLeft,
        behavior: 'smooth'
      });
    }
  }, 50);
}

// Navigation Direction: -1 (previous index / newer), 1 (next index / older)
function navigateYear(direction) {
  if (filteredWinners.length <= 1) return;

  hashInitialized = true;
  stopAndHideVideo();

  let currentIndex = filteredWinners.findIndex(w => w.year === activeYear);
  if (currentIndex === -1) return;

  let nextIndex = currentIndex + direction;
  
  // Strict boundaries (no wrap around)
  if (nextIndex < 0 || nextIndex >= filteredWinners.length) {
    return;
  }

  activeYear = filteredWinners[nextIndex].year;
  
  // Trigger UI Updates
  const yearButtons = yearSliderTrack.querySelectorAll('.btn-year');
  yearButtons.forEach(btn => {
    const year = parseInt(btn.getAttribute('data-year'));
    if (year === activeYear) {
      btn.classList.add('active');
    } else {
      btn.classList.remove('active');
    }
  });

  updateChampionCard(filteredWinners[nextIndex]);
  centerActiveYearButton();
}

// Update Filter Badge Count
function updateFilterBadge() {
  let count = 0;
  if (currentSearchQuery !== '') count++;
  if (currentDecade !== 'all') count++;
  if (currentCity !== 'all') count++;
  if (onlyLegends) count++;

  if (count > 0) {
    filterBadge.textContent = count;
    filterBadge.style.display = 'flex';
  } else {
    filterBadge.style.display = 'none';
  }
}

function getFallbackImagePath(year) {
  const yearNum = Number(year);
  if (yearNum >= 1927 && yearNum <= 1935) {
    return '/assets/images/racecourses/tandogan.jpg';
  }
  if (yearNum >= 1936 && yearNum <= 1979) {
    if (yearNum === 1968) {
      return '/assets/images/racecourses/veliefendi.jpg';
    }
    return '/assets/images/racecourses/buyuk_ankara_hipodromu.jpg';
  }
  if (yearNum >= 1980 && yearNum <= 2026) {
    return '/assets/images/racecourses/veliefendi.jpg';
  }
  return '/assets/images/temporary.jpg';
}

// Update Champion Card with Fade Transition
function updateChampionCard(winner) {
  if (!winner) return;

  logAnalyticsEvent('select_content', {
    content_type: 'champion_year',
    item_id: winner.year,
    item_name: winner.horseName
  });

  // Sync URL hash with the active year (using history.replaceState to avoid cluttering history stack)
  if (hashInitialized && window.location.hash !== `#${winner.year}`) {
    history.replaceState(null, null, `#${winner.year}`);
  }

  // Set Era Class for "Zaman Makinesi" Theme
  document.body.classList.remove('era-vintage', 'era-classic', 'era-modern', 'era-mourning');
  if (winner.year === 1938) {
    document.body.classList.add('era-mourning');
  } else if (winner.year !== 2026) {
    if (winner.year < 1960) {
      document.body.classList.add('era-vintage');
    } else if (winner.year < 2000) {
      document.body.classList.add('era-classic');
    } else {
      document.body.classList.add('era-modern');
    }
  } else {
    document.body.classList.add('era-modern');
  }

  updateArenaNavButtons(winner);

  // Show simulator button ONLY on Bold Pilot card (1996)
  const simulateRaceContainer = document.getElementById('simulate-race-container');
  if (simulateRaceContainer) {
    simulateRaceContainer.style.display = (winner.year === 1996 && !isSimulatorDismissed) ? 'flex' : 'none';
  }

  // Show poll results button ONLY on 2026 card when 2026 has run
  const pollResultsBtnContainer = document.getElementById('poll-results-btn-container');
  if (pollResultsBtnContainer) {
    pollResultsBtnContainer.style.display = (winner.year === 2026 && isGazi2026Run()) ? 'flex' : 'none';
  }

  // Reset story accordion state on year change
  const storyToggleBox = document.getElementById('story-toggle-box');
  const isDesktop = window.innerWidth > 992;
  const isShortScreen = isDesktop && window.innerHeight <= 850;
  if (storyToggleBox) {
    if (isDesktop && !isShortScreen) {
      storyToggleBox.classList.add('active');
    } else {
      storyToggleBox.classList.remove('active');
    }
  }
  if (championCard) {
    if (isDesktop && !isShortScreen) {
      championCard.classList.add('story-active');
    } else {
      championCard.classList.remove('story-active');
    }
  }

  // Add fade-out transition class
  championCardContent.classList.add('fade-out');
  championCardBg.classList.add('fade-out');

  // Load new image path with placeholder fallback
  const fallbackPath = getFallbackImagePath(winner.year);

  const targetImgSrc = (winner.year === 2026 && !isGazi2026Run()) 
    ? '/assets/images/hipodrom.jpg' 
    : getWinnerImageUrl(winner.year);
  const currentTransitionId = ++cardTransitionCounter;
  let bgApplied = false;

  const tempImg = new Image();
  tempImg.src = targetImgSrc;
  tempImg.onload = () => {
    if (currentTransitionId !== cardTransitionCounter) return;
    championCardBg.style.backgroundImage = `url('${targetImgSrc}')`;
    bgApplied = true;
  };
  tempImg.onerror = () => {
    if (currentTransitionId !== cardTransitionCounter) return;
    championCardBg.style.backgroundImage = `url('${fallbackPath}')`;
    bgApplied = true;
  };

  // Share Card Action visibility
  const btnShare = document.getElementById('btn-share-card');
  if (btnShare) {
    btnShare.style.display = (winner.year === 2026 && !isGazi2026Run()) ? 'none' : 'flex';
  }

  // Apply changes after content fades out
  setTimeout(() => {
    // Set background if tempImg hasn't finished loading yet (using the fallback)
    if (currentTransitionId === cardTransitionCounter && !bgApplied) {
      championCardBg.style.backgroundImage = `url('${fallbackPath}')`;
    }
    
    const standardUi = document.getElementById('champion-standard-ui');
    const pollUi = document.getElementById('champion-poll-ui');
    const pollDetailsUi = document.getElementById('champion-poll-details-ui');

    if (winner.year === 2026 && !isGazi2026Run()) {
      if (championCard) {
        championCard.classList.remove('poll-active');
        championCard.classList.add('poll-details-active');
      }
      if (standardUi) standardUi.style.display = 'none';
      if (pollUi) pollUi.style.display = 'none';
      if (pollDetailsUi) pollDetailsUi.style.display = 'flex';

      championYear.textContent = '2026';
      championBadge.textContent = 'TAHMİN ANKETİ';
      championBadge.className = 'badge-legend active';
      championBadge.style.background = 'var(--color-gold)';
      championBadge.style.boxShadow = '0 0 15px rgba(212, 175, 55, 0.4)';
      
      championCard.style.borderColor = 'var(--color-gold)';
      championCard.style.boxShadow = '0 20px 45px rgba(0, 0, 0, 0.7), 0 0 30px rgba(212, 175, 55, 0.15)';
    } else {
      unsubscribePoll();
      if (championCard) {
        championCard.classList.remove('poll-active');
        championCard.classList.remove('poll-details-active');
      }
      if (pollUi) pollUi.style.display = 'none';
      if (pollDetailsUi) pollDetailsUi.style.display = 'none';
      if (standardUi) standardUi.style.display = 'flex';

      // Year
      championYear.textContent = winner.year;
      
      // Name
      if (winner.coatCode) {
        championName.innerHTML = `${winner.horseNameUpper}<sup class="name-coat-code">(${winner.coatCode})</sup>`;
      } else {
        championName.textContent = winner.horseNameUpper;
      }

      // Podium Badges
      if (podiumBadges) {
        if (winner.secondPlace && winner.thirdPlace) {
          podiumBadges.style.display = 'flex';
        } else {
          podiumBadges.style.display = 'none';
        }
      }
      if (podiumBtnSecond) podiumBtnSecond.classList.remove('active');
      if (podiumBtnThird) podiumBtnThird.classList.remove('active');
      if (podiumText) {
        podiumText.textContent = '';
        podiumText.classList.remove('show');
      }
      
      // Meta values
      championTime.textContent = (activeLang === 'en' && winner.time === 'Kayıt Yok') ? 'No Record' : winner.time;
      championJockey.textContent = winner.jockey;
      if (championJockeyImg) {
        championJockeyImg.src = getJockeyImageUrl(getJockeyFilename(winner.jockey));
        championJockeyImg.alt = winner.jockey;
        championJockeyImg.style.display = '';
      }
      
      let ownerText = winner.owner || 'Bilinmiyor';
      if (activeLang === 'en') {
        if (ownerText === 'Tarih Arşivi') ownerText = 'Historical Archive';
        else if (ownerText === 'Bilinmiyor') ownerText = 'Unknown';
      }
      championOwner.textContent = ownerText;

      // Gold Prize Update
      const speedBarContainer = championCard.querySelector('.speed-bar-container');
      const metaRow = championCard.querySelector('.champion-meta-row');
      const speedBarVal = document.getElementById('speed-bar-value');
      const speedBarFill = document.getElementById('speed-bar-fill');
      const speedBarUnit = championCard.querySelector('.speed-bar-unit');

      const goldInfo = prizeGoldData[winner.year];
      if (goldInfo && goldInfo.gold) {
        if (speedBarContainer) speedBarContainer.style.display = '';
        if (metaRow) metaRow.classList.remove('no-prize-data');

        if (speedBarUnit) {
          speedBarUnit.textContent = activeLang === 'en' ? 'Gold' : 'Altın';
        }

        if (speedBarVal && speedBarFill) {
          speedBarVal.textContent = goldInfo.gold.toLocaleString(activeLang === 'en' ? 'en-US' : 'tr-TR');
          
          // Reset style width and transition immediately on change to ensure we animate from the left
          speedBarFill.style.transition = 'none';
          speedBarFill.style.width = '0%';
          
          // Force layout reflow
          speedBarFill.offsetHeight;
          
          // Set actual width with animation after content has faded in
          setTimeout(() => {
            speedBarFill.style.transition = 'width 1.2s cubic-bezier(0.25, 1, 0.5, 1)';
            let percent = goldInfo.gold / 6000;
            percent = Math.max(0, Math.min(1, percent));
            speedBarFill.style.width = `${percent * 100}%`;
          }, 150);
        }
      } else {
        if (speedBarContainer) speedBarContainer.style.display = 'none';
        if (metaRow) metaRow.classList.add('no-prize-data');

        if (speedBarVal && speedBarFill) {
          speedBarVal.textContent = '—';
          speedBarFill.style.transition = 'none';
          speedBarFill.style.width = '0%';
        }
      }
      
      // Story
      championStory.textContent = activeLang === 'en' ? getTranslatedStory(winner) : winner.story;

      // Weather info next to summary title
      const storyWeatherEl = document.getElementById('story-weather');
      if (storyWeatherEl) {
        const yearWeather = weatherData[String(winner.year)];
        if (yearWeather && yearWeather.temperature !== null) {
          const icon = getWeatherIcon(yearWeather.weather_code);
          const temp = yearWeather.temperature.toFixed(1);
          const windUnit = activeLang === 'en' ? 'km/h' : 'km/s';
          const windVal = yearWeather.wind_speed;
          const formattedWind = windVal !== null ? `• &nbsp;💨 ${windVal.toFixed(1)} ${windUnit}` : '';
          
          storyWeatherEl.innerHTML = `• &nbsp;${icon} ${temp}°C &nbsp;${formattedWind}`;
          storyWeatherEl.style.display = 'inline';
        } else {
          storyWeatherEl.style.display = 'none';
        }
      }

      // TJK Race Results & Press Clipping Links
      const hasTjkUrl = winner.tjkUrl && (winner.year !== 2026 || isGazi2026Run());
      const hasPressClipping = !!winner.pressClipping;

      if (championPressLink) {
        if (hasPressClipping) {
          championPressLink.style.display = 'inline-flex';
          championPressLink.setAttribute('data-clip', winner.pressClipping);
          championPressLink.setAttribute('data-year', winner.year);
          championPressLink.setAttribute('data-horse', winner.horseNameUpper);
        } else {
          championPressLink.style.display = 'none';
        }
      }

      if (championMoreLink) {
        if (hasTjkUrl) {
          championMoreLink.href = winner.tjkUrl;
          championMoreLink.style.display = 'inline-flex';
        } else {
          championMoreLink.style.display = 'none';
        }
      }

      if (storyLinkSeparator) {
        if (hasTjkUrl && hasPressClipping) {
          storyLinkSeparator.style.display = 'inline';
        } else {
          storyLinkSeparator.style.display = 'none';
        }
      }

      if (hasTjkUrl || hasPressClipping) {
        storyLinkContainer.style.display = 'block';
      } else {
        storyLinkContainer.style.display = 'none';
      }

      // Legend badge toggling & styling
      if (winner.isLegend) {
        const translatedLegendTitle = activeLang === 'en' ? (LEGEND_TITLES_EN[winner.legendTitle] || winner.legendTitle) : winner.legendTitle;
        championBadge.textContent = activeLang === 'en' ? `LEGEND: ${translatedLegendTitle}` : `EFSANE: ${winner.legendTitle}`;
        championBadge.className = 'badge-legend active';
        championBadge.style.background = winner.accentColor || 'var(--color-gold)';
        championBadge.style.boxShadow = `0 0 15px ${winner.accentColor || 'var(--color-gold)'}60`;
        
        // Update glow accent on card frame
        championCard.style.borderColor = winner.accentColor || 'rgba(255, 255, 255, 0.06)';
        championCard.style.boxShadow = `0 20px 45px rgba(0, 0, 0, 0.7), 0 0 30px ${winner.accentColor}20`;
      } else {
        championBadge.className = 'badge-legend';
        championCard.style.borderColor = 'rgba(255, 255, 255, 0.06)';
        championCard.style.boxShadow = 'var(--shadow-premium)';
      }
    }

    // Video Link & Preview Thumbnail
    if (winner.video && (winner.year !== 2026 || isGazi2026Run())) {
      const regExp = /^.*(youtu.be\/|v\/|u\/\w\/|embed\/|watch\?v=|\&v=)([^#\&\?]*).*/;
      const match = winner.video.match(regExp);
      const videoId = (match && match[2].length === 11) ? match[2] : null;
      
      if (videoId) {
        const isMobile = window.innerWidth <= 768;
        const thumbImg = isMobile ? 'default.jpg' : 'mqdefault.jpg';
        previewThumbnail.style.backgroundImage = `url('https://img.youtube.com/vi/${videoId}/${thumbImg}')`;
        championVideoPreview.setAttribute('data-video-id', videoId);
        championVideoPreview.style.display = 'block';
      } else {
        championVideoPreview.style.display = 'none';
      }
    } else {
      championVideoPreview.style.display = 'none';
    }

    // Fade-in new content
    championCardContent.classList.remove('fade-out');
    championCardBg.classList.remove('fade-out');

    // Preload next and previous years' images when browser is idle to speed up timeline navigation
    if ('requestIdleCallback' in window) {
      requestIdleCallback(() => preloadAdjacentWinners(winner));
    } else {
      setTimeout(() => preloadAdjacentWinners(winner), 400);
    }
  }, 200);
}

// Preload adjacent winners' images (previous and next in filteredWinners) to speed up timeline navigation
function preloadAdjacentWinners(currentWinner) {
  if (!currentWinner || filteredWinners.length <= 1) return;
  
  const currentIndex = filteredWinners.findIndex(w => w.year === currentWinner.year);
  if (currentIndex === -1) return;

  const adjacentIndices = [];
  if (currentIndex > 0) adjacentIndices.push(currentIndex - 1);
  if (currentIndex < filteredWinners.length - 1) adjacentIndices.push(currentIndex + 1);

  adjacentIndices.forEach(idx => {
    const winner = filteredWinners[idx];
    const targetImgSrc = (winner.year === 2026 && !isGazi2026Run()) 
      ? '/assets/images/hipodrom.jpg' 
      : getWinnerImageUrl(winner.year);
    
    // Create new image object to trigger preloading
    const img = new Image();
    img.src = targetImgSrc;
    
    // Also preload jockey image if available
    if (winner.jockey && winner.jockey !== '-' && winner.jockey !== 'Kayıt Yok') {
      const jockeyImgName = getJockeyFilename(winner.jockey);
      const jockeyImg = new Image();
      jockeyImg.src = getJockeyImageUrl(jockeyImgName);
    }
  });
}

// Firestore'dan 2026 anketinin aktiflik durumunu yükleyen ve tohumlayan fonksiyon


// Show No Results Card fallback
function showNoResultsCard() {
  championCardContent.classList.add('fade-out');
  championCardBg.classList.add('fade-out');

  setTimeout(() => {
    document.body.classList.remove('era-vintage', 'era-classic', 'era-modern', 'era-mourning');
    championCardBg.style.backgroundImage = 'none';
    championYear.textContent = '----';
    championName.textContent = activeLang === 'en' ? 'NO RESULTS FOUND' : 'SONUÇ BULUNAMADI';
    championTime.textContent = '-';
    championJockey.textContent = '-';
    if (championJockeyImg) {
      championJockeyImg.style.display = 'none';
    }
    championOwner.textContent = '-';
    
    // Clear speed/prize bar elements
    const speedBarContainer = document.querySelector('.speed-bar-container');
    const metaRow = document.querySelector('.champion-meta-row');
    if (speedBarContainer) speedBarContainer.style.display = 'none';
    if (metaRow) metaRow.classList.add('no-prize-data');

    const speedBarVal = document.getElementById('speed-bar-value');
    const speedBarFill = document.getElementById('speed-bar-fill');
    if (speedBarVal) {
      speedBarVal.textContent = '—';
    }
    if (speedBarFill) {
      speedBarFill.style.transition = 'none';
      speedBarFill.style.width = '0%';
    }

    // Hide podium badges and clear text
    const podiumBadges = document.getElementById('podium-badges');
    const podiumText = document.getElementById('podium-text');
    if (podiumBadges) {
      podiumBadges.style.display = 'none';
    }
    if (podiumText) {
      podiumText.textContent = '';
      podiumText.classList.remove('show');
    }

    // Hide simulator button
    const simulateRaceContainer = document.getElementById('simulate-race-container');
    if (simulateRaceContainer) {
      simulateRaceContainer.style.display = 'none';
    }
    
    if (storyLinkContainer) {
      storyLinkContainer.style.display = 'none';
    }
    
    // Add "Clear Filters" button inside story
    if (activeLang === 'en') {
      championStory.innerHTML = `
        No Gazi Derby champion matches your filter criteria.
        <br><br>
        <button class="btn-clear-search-card" id="btn-clear-search-card">Clear Filters</button>
      `;
    } else {
      championStory.innerHTML = `
        Filtre kriterlerinize uyan Gazi Koşusu şampiyonu bulunmamaktadır.
        <br><br>
        <button class="btn-clear-search-card" id="btn-clear-search-card">Filtreleri Temizle</button>
      `;
    }

    const clearBtn = document.getElementById('btn-clear-search-card');
    if (clearBtn) {
      clearBtn.addEventListener('click', () => {
        resetToInitialState();
      });
    }

    championBadge.className = 'badge-legend';
    championVideoPreview.style.display = 'none';
    championCard.style.borderColor = 'rgba(255, 255, 255, 0.06)';
    championCard.style.boxShadow = 'var(--shadow-premium)';

    championCardContent.classList.remove('fade-out');
    championCardBg.classList.remove('fade-out');
  }, 200);
}

// Play YouTube Video Inline using Iframe API
function playVideoInline(videoId) {
  if (ytPlayer && typeof ytPlayer.loadVideoById === 'function') {
    ytPlayer.loadVideoById({
      videoId: videoId,
      playerVars: {
        autoplay: 1,
        controls: 0,
        rel: 0,
        showinfo: 0,
        iv_load_policy: 3,
        modestbranding: 1,
        disablekb: 1,
        enablejsapi: 1
      }
    });
    championVideoContainer.classList.add('active');
    document.body.classList.add('lights-out');
    togglePlayPauseIcons(true);
  } else {
    // Initialize YouTube player replacing the placeholder div
    ytPlayer = new YT.Player('champion-video-iframe-placeholder', {
      height: '100%',
      width: '100%',
      videoId: videoId,
      playerVars: {
        autoplay: 1,
        controls: 0,
        rel: 0,
        showinfo: 0,
        iv_load_policy: 3,
        modestbranding: 1,
        disablekb: 1,
        enablejsapi: 1
      },
      events: {
        onReady: (event) => {
          event.target.playVideo();
          championVideoContainer.classList.add('active');
          document.body.classList.add('lights-out');
          togglePlayPauseIcons(true);
          toggleVolumeIcons(event.target.isMuted());
        },
        onStateChange: (event) => {
          if (event.data === 1) { // Playing
            togglePlayPauseIcons(true);
          } else if (event.data === 2) { // Paused
            togglePlayPauseIcons(false);
          }
        }
      }
    });
  }
}

// Stop Video Playing and Hide Overlay
function stopAndHideVideo() {
  championVideoContainer.classList.remove('active');
  document.body.classList.remove('lights-out');
  if (ytPlayer && typeof ytPlayer.stopVideo === 'function') {
    ytPlayer.stopVideo();
  }
  togglePlayPauseIcons(false);
}

// Control Bar Icon Toggles
function togglePlayPauseIcons(isPlaying) {
  if (iconPause && iconPlay) {
    if (isPlaying) {
      iconPause.style.display = 'block';
      iconPlay.style.display = 'none';
    } else {
      iconPause.style.display = 'none';
      iconPlay.style.display = 'block';
    }
  }
}

function toggleVolumeIcons(isMuted) {
  if (iconVolumeOn && iconVolumeOff) {
    if (isMuted) {
      iconVolumeOn.style.display = 'none';
      iconVolumeOff.style.display = 'block';
    } else {
      iconVolumeOn.style.display = 'block';
      iconVolumeOff.style.display = 'none';
    }
  }
}

// Render verified statistics in the sidebar
function renderStatistics() {
  const statRaces = document.getElementById('stat-races');
  const statRecord = document.getElementById('stat-record');
  const statJockey = document.getElementById('stat-jockey');
  const statStreak = document.getElementById('stat-streak');
  const statOwner = document.getElementById('stat-owner');
  const statFirst = document.getElementById('stat-first');

  if (statRaces) statRaces.textContent = `${statistics.totalRaces} Koşu`;
  if (statRecord) statRecord.textContent = statistics.recordHolder;
  if (statJockey) statJockey.textContent = statistics.mostWinsJockey;
  if (statStreak) statStreak.textContent = statistics.consecutiveWinsJockey;
  if (statOwner) statOwner.textContent = statistics.mostWinsOwner;
  if (statFirst) statFirst.textContent = statistics.firstWinner;
}

// Keep the sidebar toggle button's active state in sync with sidebar panel visibility
function updateSidebarButtonState() {
  const isMobile = window.innerWidth <= 992;
  if (isMobile) {
    if (sidebarPanel && sidebarPanel.classList.contains('active')) {
      sidebarToggleBtn.classList.add('active');
    } else {
      sidebarToggleBtn.classList.remove('active');
    }
  } else {
    if (sidebarPanel && sidebarPanel.classList.contains('collapsed')) {
      sidebarToggleBtn.classList.remove('active');
      if (floatingInfoBtn) {
        floatingInfoBtn.classList.remove('sidebar-open');
      }
    } else {
      sidebarToggleBtn.classList.add('active');
      if (floatingInfoBtn) {
        floatingInfoBtn.classList.add('sidebar-open');
      }
    }
  }
}

// Setup Tinder-style swipe gestures on mobile/tablet viewports
function setupSwipeGestures() {
  if (!championCard) return;

  let startX = 0;
  let startY = 0;
  let deltaX = 0;
  let deltaY = 0;
  let isDragging = false;
  const threshold = 80; // Minimum swipe distance in px to trigger navigation

  championCard.addEventListener('touchstart', (e) => {
    if (e.touches.length !== 1) return;
    
    startX = e.touches[0].clientX;
    startY = e.touches[0].clientY;
    deltaX = 0;
    deltaY = 0;
    isDragging = true;
    
    // Disable card transition dynamically for real-time drag response
    championCard.style.transition = 'none';
  }, { passive: true });

  championCard.addEventListener('touchmove', (e) => {
    if (!isDragging) return;

    deltaX = e.touches[0].clientX - startX;
    deltaY = e.touches[0].clientY - startY;

    // Only swipe if horizontal drag is dominant
    if (Math.abs(deltaX) > Math.abs(deltaY)) {
      if (e.cancelable) e.preventDefault();
      
      // Tinder tilt: 1 degree of tilt for every 25px dragged
      const rotation = deltaX * 0.04;
      
      // Apply real-time translation and rotation tilt
      championCard.style.transform = `translate3d(${deltaX}px, 0, 0) rotate(${rotation}deg)`;
    }
  }, { passive: false });

  championCard.addEventListener('touchend', () => {
    if (!isDragging) return;
    isDragging = false;

    // Restore smooth CSS transition rules
    championCard.style.transition = '';

    if (Math.abs(deltaX) > threshold) {
      if (deltaX > 0) {
        // Swipe Right -> previous (newer) year
        navigateYear(-1);
      } else {
        // Swipe Left -> next (older) year
        navigateYear(1);
      }
    }

    // Smoothly snap back to the center stage
    championCard.style.transform = '';
  });

  championCard.addEventListener('touchcancel', () => {
    isDragging = false;
    championCard.style.transition = '';
    championCard.style.transform = '';
  });
}

// Show/Hide Year Slider navigation arrows (< and >) based on scroll boundaries
function updateSliderNavButtons() {
  if (!yearSliderTrack || !sliderPrevBtn || !sliderNextBtn) return;

  const scrollLeft = yearSliderTrack.scrollLeft;
  const maxScrollLeft = yearSliderTrack.scrollWidth - yearSliderTrack.clientWidth;

  // Toggle opacity and interaction of the navigation buttons
  if (scrollLeft <= 5) {
    sliderPrevBtn.style.opacity = '0';
    sliderPrevBtn.style.pointerEvents = 'none';
  } else {
    sliderPrevBtn.style.opacity = '1';
    sliderPrevBtn.style.pointerEvents = 'auto';
  }

  if (scrollLeft >= maxScrollLeft - 5 || maxScrollLeft <= 0) {
    sliderNextBtn.style.opacity = '0';
    sliderNextBtn.style.pointerEvents = 'none';
  } else {
    sliderNextBtn.style.opacity = '1';
    sliderNextBtn.style.pointerEvents = 'auto';
  }
}

// Show/Hide Arena navigation arrows based on boundary check
function updateArenaNavButtons(winner) {
  if (!arenaPrevBtn || !arenaNextBtn || filteredWinners.length === 0) return;

  const currentIndex = filteredWinners.findIndex(w => w.year === winner.year);
  if (currentIndex === -1) return;

  // Left arrow (arenaPrevBtn) -> goes to newer year (index decreases)
  if (currentIndex === 0) {
    arenaPrevBtn.style.opacity = '0';
    arenaPrevBtn.style.pointerEvents = 'none';
  } else {
    arenaPrevBtn.style.opacity = '1';
    arenaPrevBtn.style.pointerEvents = 'auto';
  }

  // Right arrow (arenaNextBtn) -> goes to older year (index increases)
  if (currentIndex === filteredWinners.length - 1) {
    arenaNextBtn.style.opacity = '0';
    arenaNextBtn.style.pointerEvents = 'none';
  } else {
    arenaNextBtn.style.opacity = '1';
    arenaNextBtn.style.pointerEvents = 'auto';
  }
}

// Intro Animation Sequence
function setupIntroAnimation() {
  const hasSeenIntro = localStorage.getItem('seenGaziIntro');

  if (hasSeenIntro) {
    return; // Do not run if already seen
  }

  const overlay = document.getElementById('gazi-intro-overlay');
  const skipBtn = document.getElementById('intro-skip-btn');
  const yearsContainer = document.getElementById('intro-years-container');
  const yearDisplay = document.getElementById('intro-year-display');
  const horseDisplay = document.getElementById('intro-horse-display');
  const showcase = document.getElementById('intro-showcase');
  const actionBtn = document.getElementById('intro-action-btn');

  if (!overlay) return;

  // Show intro and lock body scrolling
  overlay.style.display = 'flex';
  document.body.style.overflow = 'hidden';
  document.documentElement.style.overflow = 'hidden';

  let currentYear = 1927;
  let targetYear = 2025;
  let tickerTimeout = null;

  // Web Audio Context for ticking sound
  let audioCtx = null;
  function playTickSound() {
    try {
      if (!audioCtx) {
        audioCtx = new (window.AudioContext || window.webkitAudioContext)();
      }
      if (audioCtx.state === 'suspended') {
        audioCtx.resume();
      }
      const osc = audioCtx.createOscillator();
      const gain = audioCtx.createGain();
      
      osc.connect(gain);
      gain.connect(audioCtx.destination);
      
      osc.frequency.setValueAtTime(800, audioCtx.currentTime); // High pitch click
      gain.gain.setValueAtTime(0.015, audioCtx.currentTime); // Quiet click
      gain.gain.exponentialRampToValueAtTime(0.00001, audioCtx.currentTime + 0.04); // Rapid decay
      
      osc.start();
      osc.stop(audioCtx.currentTime + 0.04);
    } catch (e) {
      // Audio autoplay restrictions might block this, fail silently
    }
  }

  function tick() {
    if (isGazi2026Run()) {
      targetYear = 2026;
    }
    const winnerForYear = gaziWinners.find(w => w.year === currentYear);
    const horseName = winnerForYear ? winnerForYear.horseNameUpper : '';
    yearDisplay.textContent = currentYear;
    if (horseDisplay) horseDisplay.textContent = horseName;
    playTickSound();

    if (currentYear < targetYear) {
      currentYear++;
      
      // Calculate dynamic delay for speed variation (easing)
      let delay = 12; // Peak fast speed
      const totalRaces = targetYear - 1927;
      const progress = (currentYear - 1927) / totalRaces;

      if (progress < 0.15) {
        // Accelerating at start: 50ms down to 12ms
        delay = 50 - (progress / 0.15) * 38;
      } else if (progress > 0.85) {
        // Decelerating at end: 12ms up to 200ms
        const endProgress = (progress - 0.85) / 0.15;
        delay = 12 + Math.pow(endProgress, 2.5) * 220;
      }

      tickerTimeout = setTimeout(tick, delay);
    } else {
      // Sequence completed! Transition to 100th year showcase.
      setTimeout(() => {
        yearsContainer.style.transition = 'opacity 0.6s ease, transform 0.6s ease';
        yearsContainer.style.opacity = '0';
        yearsContainer.style.transform = 'scale(0.8)';
        
        setTimeout(() => {
          yearsContainer.style.display = 'none';
          showcase.style.display = 'flex';
        }, 600);
      }, 600);
    }
  }

  // Start ticking
  tick();

  // Exit overlay handler
  function exitIntro() {
    if (tickerTimeout) clearTimeout(tickerTimeout);
    localStorage.setItem('seenGaziIntro', 'true');
    overlay.classList.add('fade-out-intro');
    
    // Unlock scrolling
    document.body.style.overflow = '';
    document.documentElement.style.overflow = '';

    setTimeout(() => {
      overlay.remove();
    }, 800);
  }

  // Wire up buttons
  skipBtn.addEventListener('click', exitIntro);
  actionBtn.addEventListener('click', exitIntro);
}

// Display Premium Toast Notification
function showToast(message) {
  let toast = document.getElementById('app-toast');
  if (!toast) {
    toast = document.createElement('div');
    toast.id = 'app-toast';
    toast.className = 'toast-notification';
    document.body.appendChild(toast);
  }
  toast.textContent = message;
  toast.classList.remove('active'); // Reset state if clicked multiple times
  void toast.offsetWidth; // Trigger reflow to restart animation
  toast.classList.add('active');
  
  // Auto dismiss after 2.5 seconds
  setTimeout(() => {
    toast.classList.remove('active');
  }, 2500);
}

// Generate the Champion Card as an image blob and share it (Web Share API for files)
function generateAndShareCard(winner) {
  const btn = document.getElementById('btn-share-card');
  const loader = document.getElementById('share-loader');
  const icon = document.getElementById('share-icon');
  
  if (!btn || btn.disabled) return;
  
  // Set Loading state
  btn.disabled = true;
  if (loader) loader.style.display = 'inline-block';
  if (icon) icon.style.display = 'none';

  const fallbackPath = getFallbackImagePath(winner.year);
  const targetImgSrc = winner.year === 2026 
    ? '/assets/images/hipodrom.jpg' 
    : getWinnerImageUrl(winner.year);
  const imageName = (winner.horseName || winner.horseNameUpper || 'sampiyon')
    .toLowerCase()
    .replace(/\s+/g, '_')
    .replace(/[^a-z0-9_]/g, '');

  const bgImg = new Image();
  bgImg.crossOrigin = 'anonymous';

  const logoImg = new Image();
  logoImg.crossOrigin = 'anonymous';

  function drawAndProcess(loadedImg, loadedLogo) {
    try {
      const canvas = document.createElement('canvas');
      canvas.width = 1200;
      canvas.height = 800;
      const ctx = canvas.getContext('2d');

      // 1. Draw Background (Cover Aspect Ratio)
      const imgWidth = loadedImg.width || 1200;
      const imgHeight = loadedImg.height || 800;
      const canvasWidth = canvas.width;
      const canvasHeight = canvas.height;
      
      const imgRatio = imgWidth / imgHeight;
      const canvasRatio = canvasWidth / canvasHeight;
      
      let drawWidth, drawHeight, drawX, drawY;
      
      if (imgRatio > canvasRatio) {
        drawHeight = canvasHeight;
        drawWidth = canvasHeight * imgRatio;
        drawX = (canvasWidth - drawWidth) / 2;
        drawY = 0;
      } else {
        drawWidth = canvasWidth;
        drawHeight = canvasWidth / imgRatio;
        drawX = 0;
        drawY = (canvasHeight - drawHeight) / 2;
      }
      
      ctx.drawImage(loadedImg, drawX, drawY, drawWidth, drawHeight);

      // 2. Premium Gradient Overlay
      const gradient = ctx.createLinearGradient(0, 0, 0, canvasHeight);
      gradient.addColorStop(0, 'rgba(3, 7, 5, 0.45)');
      gradient.addColorStop(0.4, 'rgba(3, 7, 5, 0.7)');
      gradient.addColorStop(1, 'rgba(3, 7, 5, 0.98)');
      ctx.fillStyle = gradient;
      ctx.fillRect(0, 0, canvasWidth, canvasHeight);

      // 3. Elegant Gold Frame
      ctx.strokeStyle = '#d4af37';
      ctx.lineWidth = 14;
      ctx.strokeRect(7, 7, canvasWidth - 14, canvasHeight - 14);

      // Thin inner line
      ctx.strokeStyle = 'rgba(255, 255, 255, 0.05)';
      ctx.lineWidth = 1;
      ctx.strokeRect(20, 20, canvasWidth - 40, canvasHeight - 40);

      // 4. Branding Logo
      ctx.textAlign = 'left';
      ctx.textBaseline = 'top';
      if (loadedLogo) {
        ctx.drawImage(loadedLogo, 60, 44, 110, 44);
      } else {
        ctx.font = 'bold 24px "Outfit", sans-serif';
        ctx.fillStyle = '#d4af37';
        ctx.fillText('GAZİ', 60, 50);
        ctx.fillStyle = '#ffffff';
        ctx.fillText('DERBY', 122, 50);
      }

      // 5. Year and Legend Badge
      ctx.font = '900 110px "Outfit", sans-serif';
      ctx.fillStyle = 'rgba(255, 255, 255, 0.15)';
      ctx.fillText(winner.year, 60, 110);

      if (winner.isLegend) {
        const badgeText = `EFSANE: ${winner.legendTitle.toUpperCase()}`;
        ctx.font = 'bold 11px "Outfit", sans-serif';
        const badgeWidth = ctx.measureText(badgeText).width + 24;
        
        ctx.fillStyle = winner.accentColor || '#d4af37';
        ctx.fillRect(360, 155, badgeWidth, 32);
        
        ctx.fillStyle = '#030705';
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';
        ctx.fillText(badgeText, 360 + (badgeWidth / 2), 155 + 16);
        ctx.textAlign = 'left';
        ctx.textBaseline = 'top';
      }

      // 6. Champion Name
      ctx.font = '800 64px "Outfit", sans-serif';
      ctx.fillStyle = '#ffffff';
      ctx.shadowColor = 'rgba(0, 0, 0, 0.5)';
      ctx.shadowBlur = 10;
      ctx.fillText(winner.horseNameUpper, 60, 240);
      ctx.shadowBlur = 0; // Reset shadow

      // 7. Meta details columns (3 or 4 columns depending on prize data)
      const startY = 340;
      const startX = 60;
      
      const goldInfo = prizeGoldData[winner.year];
      const hasPrizeData = !!(goldInfo && goldInfo.gold);
      
      if (hasPrizeData) {
        // 4 Columns layout
        const colWidth = 245;
        const spacing = 33;

        // Card 1: DERECE / TIME
        const labelTime = translations[activeLang]['card_label_time'];
        drawMetaBox(ctx, startX, startY, colWidth, 120, labelTime, winner.time, true);

        // Card 2: İKRAMİYE (ALTIN) / PRIZE (GOLD)
        const labelPrize = translations[activeLang]['card_label_prize'];
        const prizeText = `${goldInfo.gold.toLocaleString(activeLang === 'en' ? 'en-US' : 'tr-TR')} ${activeLang === 'en' ? 'Gold' : 'Altın'}`;
        drawMetaBox(ctx, startX + colWidth + spacing, startY, colWidth, 120, labelPrize, prizeText, true);

        // Card 3: JOKEY / JOCKEY
        const labelJockey = translations[activeLang]['card_label_jockey'];
        drawMetaBox(ctx, startX + 2 * (colWidth + spacing), startY, colWidth, 120, labelJockey, winner.jockey, false);

        // Card 4: SAHİP / OWNER
        const labelOwner = translations[activeLang]['card_label_owner'];
        const ownerVal = winner.owner || (activeLang === 'en' ? 'Unknown' : 'Bilinmiyor');
        drawMetaBox(ctx, startX + 3 * (colWidth + spacing), startY, colWidth, 120, labelOwner, ownerVal, false);
      } else {
        // 3 Columns layout
        const colWidth = 338;
        const spacing = 33;

        // Card 1: DERECE / TIME
        const labelTime = translations[activeLang]['card_label_time'];
        drawMetaBox(ctx, startX, startY, colWidth, 120, labelTime, winner.time, true);

        // Card 2: JOKEY / JOCKEY
        const labelJockey = translations[activeLang]['card_label_jockey'];
        drawMetaBox(ctx, startX + colWidth + spacing, startY, colWidth, 120, labelJockey, winner.jockey, false);

        // Card 3: SAHİP / OWNER
        const labelOwner = translations[activeLang]['card_label_owner'];
        const ownerVal = winner.owner || (activeLang === 'en' ? 'Unknown' : 'Bilinmiyor');
        drawMetaBox(ctx, startX + 2 * (colWidth + spacing), startY, colWidth, 120, labelOwner, ownerVal, false);
      }

      // 8. Story Description
      ctx.fillStyle = '#a7b2ad';
      ctx.font = '500 20px "Plus Jakarta Sans", sans-serif';
      const storyText = activeLang === 'en' ? getTranslatedStory(winner) : winner.story;
      wrapText(ctx, storyText, 60, 500, 1080, 32);

      // 9. Footer
      ctx.fillStyle = 'rgba(255, 255, 255, 0.2)';
      ctx.font = '14px "Plus Jakarta Sans", sans-serif';
      const footerText = activeLang === 'en' 
        ? 'gaziderby.com - Gazi Derby Interactive Timeline' 
        : 'gaziderby.com - Gazi Koşusu İnteraktif Zaman Tüneli';
      ctx.fillText(footerText, 60, 720);

      // 10. Process and Share Blob
      canvas.toBlob((blob) => {
        if (!blob) {
          throw new Error('Canvas blob generation failed');
        }

        const fileName = `${winner.year}_${imageName}.jpg`;
        const file = new File([blob], fileName, { type: 'image/jpeg' });
        const shareLink = `${window.location.origin}/#${winner.year}`;
        const shareText = activeLang === 'en'
          ? `${winner.year} Gazi Derby Champion ${winner.horseName}! Jockey: ${winner.jockey}, Time: ${winner.time}. Details: ${shareLink}`
          : `${winner.year} yılı Gazi Koşusu Şampiyonu ${winner.horseName}! Jokey: ${winner.jockey}, Derece: ${winner.time}. Detaylar: ${shareLink}`;

        const shareTitle = activeLang === 'en'
          ? `${winner.horseName} - Gazi Derby Champion`
          : `${winner.horseName} - Gazi Koşusu Şampiyonu`;

        // Attempt sharing the image file natively
        if (navigator.canShare && navigator.canShare({ files: [file] })) {
          navigator.share({
            files: [file],
            title: shareTitle,
            text: shareText
          })
          .catch(() => {
            // User cancelled
          })
          .finally(() => {
            resetButton();
          });
        } else {
          // File share not supported, fall back to URL sharing or Clipboard Copy
          if (navigator.share) {
            navigator.share({
              title: shareTitle,
              text: shareText,
              url: shareLink
            })
            .catch(() => {})
            .finally(() => {
              resetButton();
            });
          } else {
            // Copy link to clipboard
            const copyText = shareText;
            navigator.clipboard.writeText(copyText).then(() => {
              const toastMsg = activeLang === 'en'
                ? 'Share link copied to clipboard!'
                : 'Paylaşım linki panoya kopyalandı!';
              showToast(toastMsg);
            }).catch(() => {
              // Older text area copy fallback
              const textArea = document.createElement('textarea');
              textArea.value = copyText;
              textArea.style.position = 'fixed';
              document.body.appendChild(textArea);
              textArea.focus();
              textArea.select();
              try {
                document.execCommand('copy');
                const toastMsg = activeLang === 'en'
                  ? 'Share link copied to clipboard!'
                  : 'Paylaşım linki panoya kopyalandı!';
                showToast(toastMsg);
              } catch (err) {
                const toastMsg = activeLang === 'en'
                  ? 'Copying failed.'
                  : 'Kopyalama başarısız oldu.';
                showToast(toastMsg);
              }
              document.body.removeChild(textArea);
            })
            .finally(() => {
              resetButton();
            });
          }

          // Trigger auto-download of the card image as a premium fallback!
          const link = document.createElement('a');
          link.download = fileName;
          link.href = URL.createObjectURL(blob);
          link.click();
          const toastMsg = activeLang === 'en'
            ? 'Share link copied & champion card downloaded!'
            : 'Paylaşım linki kopyalandı & şampiyon kartı indirildi!';
          showToast(toastMsg);
        }
      }, 'image/jpeg', 0.95);

    } catch (err) {
      console.error(err);
      const toastMsg = activeLang === 'en'
        ? 'An error occurred while preparing sharing.'
        : 'Paylaşım hazırlanırken bir hata oluştu.';
      showToast(toastMsg);
      resetButton();
    }
  }

  function resetButton() {
    btn.disabled = false;
    if (loader) loader.style.display = 'none';
    if (icon) icon.style.display = 'inline-block';
  }

  // Draw Meta Box helper
  function drawMetaBox(context, x, y, w, h, label, val, isGold) {
    context.fillStyle = 'rgba(255, 255, 255, 0.02)';
    context.fillRect(x, y, w, h);
    context.strokeStyle = 'rgba(255, 255, 255, 0.06)';
    context.lineWidth = 1;
    context.strokeRect(x, y, w, h);

    context.fillStyle = '#5e6b65';
    context.font = 'bold 11px "Plus Jakarta Sans", sans-serif';
    context.fillText(label, x + 18, y + 22);

    // Dynamic Font Sizing to prevent text overflow
    let baseFontSize = isGold ? 25 : 19;
    let fontFam = isGold ? '"Outfit", sans-serif' : '"Plus Jakarta Sans", sans-serif';
    context.font = `bold ${baseFontSize}px ${fontFam}`;
    
    const maxTextWidth = w - 36;
    let textWidth = context.measureText(val).width;
    while (textWidth > maxTextWidth && baseFontSize > 11) {
      baseFontSize -= 1;
      context.font = `bold ${baseFontSize}px ${fontFam}`;
      textWidth = context.measureText(val).width;
    }

    if (isGold) {
      context.fillStyle = '#fbe69e'; // gold-light
    } else {
      context.fillStyle = '#ffffff';
    }
    context.fillText(val, x + 18, y + 60);
  }

  // Wrap Text Helper
  function wrapText(context, textStr, x, y, maxWidth, lineHeight) {
    const words = textStr.split(' ');
    let line = '';
    let currentY = y;
    
    for (let n = 0; n < words.length; n++) {
      let testLine = line + words[n] + ' ';
      let metrics = context.measureText(testLine);
      let testWidth = metrics.width;
      if (testWidth > maxWidth && n > 0) {
        context.fillText(line, x, currentY);
        line = words[n] + ' ';
        currentY += lineHeight;
      } else {
        line = testLine;
      }
    }
    context.fillText(line, x, currentY);
  }

  function loadBg() {
    bgImg.onload = () => drawAndProcess(bgImg, logoImg.complete && logoImg.naturalWidth ? logoImg : null);
    bgImg.onerror = () => {
      // Fallback if target year image fails
      const fallbackImg = new Image();
      fallbackImg.crossOrigin = 'anonymous';
      fallbackImg.onload = () => drawAndProcess(fallbackImg, logoImg.complete && logoImg.naturalWidth ? logoImg : null);
      fallbackImg.onerror = () => {
        // Extreme blank canvas fallback
        const tempCanvas = document.createElement('canvas');
        tempCanvas.width = 1200;
        tempCanvas.height = 800;
        const tc = tempCanvas.getContext('2d');
        tc.fillStyle = '#060e0a';
        tc.fillRect(0, 0, 1200, 800);
        drawAndProcess(tempCanvas, logoImg.complete && logoImg.naturalWidth ? logoImg : null);
      };
      fallbackImg.src = fallbackPath;
    };
    bgImg.src = targetImgSrc;
  }

  logoImg.onload = () => loadBg();
  logoImg.onerror = () => loadBg();
  logoImg.src = '/assets/images/logo.png';
}

/* ==========================================================================
   İstatistik ve Analiz Sekmesi İş Mantığı
   ========================================================================== */

// Sekme Değiştirme Fonksiyonu
function switchTab(tab) {
  if (activeTab === tab) return;
  activeTab = tab;

  const statsCardSidebar = document.querySelector('.stats-card-sidebar');
  const mTimeline = document.getElementById('mobile-nav-timeline-btn');
  const mStats = document.getElementById('mobile-nav-stats-btn');

  if (tab === 'timeline') {
    if (navTimelineBtn) navTimelineBtn.classList.add('active');
    if (navStatsBtn) navStatsBtn.classList.remove('active');
    if (mTimeline) mTimeline.classList.add('active');
    if (mStats) mStats.classList.remove('active');

    if (mainStage) mainStage.style.display = 'flex';
    if (statsStage) statsStage.style.display = 'none';
    if (statsCardSidebar) statsCardSidebar.style.display = ''; // Show
    
    stopAndHideVideo();

    // Re-apply correct Era class for "Zaman Makinesi" when returning to timeline
    const currentYear = Number(championYear.textContent);
    document.body.classList.remove('era-vintage', 'era-classic', 'era-modern', 'era-mourning');
    if (currentYear === 1938) {
      document.body.classList.add('era-mourning');
    } else if (currentYear) {
      if (currentYear < 1960) {
        document.body.classList.add('era-vintage');
      } else if (currentYear < 2000) {
        document.body.classList.add('era-classic');
      } else {
        document.body.classList.add('era-modern');
      }
    }
  } else {
    if (navTimelineBtn) navTimelineBtn.classList.remove('active');
    if (navStatsBtn) navStatsBtn.classList.add('active');
    if (mTimeline) mTimeline.classList.remove('active');
    if (mStats) mStats.classList.add('active');

    if (mainStage) mainStage.style.display = 'none';
    if (statsStage) statsStage.style.display = 'flex';
    if (statsCardSidebar) statsCardSidebar.style.display = 'none'; // Hide
    
    stopAndHideVideo();
    
    // Remove era classes for Stats tab (always default to modern dark for chart legibility)
    document.body.classList.remove('era-vintage', 'era-classic', 'era-modern', 'era-mourning');

    renderStatsDashboard();
  }
}

// Dynamic stats subtitle updater based on active filters
function updateStatsSubtitle() {
  const subtitleEl = document.querySelector('[data-i18n="stats_header_subtitle"]');
  if (!subtitleEl) return;

  const isFiltered = currentSearchQuery || currentDecade !== 'all' || currentCity !== 'all' || onlyLegends;

  if (isFiltered) {
    const filterParts = [];
    if (onlyLegends) {
      filterParts.push(activeLang === 'en' ? 'Legends' : 'Efsaneler');
    }
    if (currentCity !== 'all') {
      const cityTranslated = activeLang === 'en' ? (currentCity === 'İstanbul' ? 'Istanbul' : 'Ankara') : currentCity;
      filterParts.push(cityTranslated);
    }
    if (currentDecade !== 'all') {
      filterParts.push(activeLang === 'en' ? `${currentDecade} Era` : `${currentDecade} Dönemi`);
    }
    if (currentSearchQuery) {
      filterParts.push(activeLang === 'en' ? `Search: "${currentSearchQuery}"` : `Arama: "${currentSearchQuery}"`);
    }

    subtitleEl.textContent = activeLang === 'en'
      ? `Speed trends and success rankings of filtered races (Filters: ${filterParts.join(', ')})`
      : `Filtrelenmiş koşuların hız trendleri ve başarı sıralamaları (Filtre: ${filterParts.join(', ')})`;
  } else {
    subtitleEl.textContent = activeLang === 'en'
      ? "Speed trends and success rankings of all races from 1927 to present"
      : "1927'den günümüze tüm koşuların hız trendleri ve başarı sıralamaları";
  }
}

// Analiz Paneli Güncelleme / Çizme Tetikleyicisi
function renderStatsDashboard() {
  updateStatsCards();
  renderBarCharts();
  updateStatsSubtitle();

  if (activeTab === 'stats') {
    // Canvas boyutunun flex görünürlüğünden sonra güncellenmesi için next frame beklenir
    requestAnimationFrame(() => {
      drawTimeTrendChart();
    });
  }
}

// Dinamik Stat Kartları Güncelleyicisi
function updateStatsCards() {
  const cardsGrid = document.querySelector('.stats-cards-grid');
  if (!cardsGrid) return;

  if (!filteredWinners || filteredWinners.length === 0) {
    cardsGrid.style.opacity = '0.5';
    return;
  }
  cardsGrid.style.opacity = '1';

  // Exclude 2026 (or poll years) from statistics/analysis
  const statsWinners = filteredWinners.filter(w => w.year !== 2026 || isGazi2026Run());

  if (statsTotalYears) {
    statsTotalYears.textContent = `${statsWinners.length} ${activeLang === 'en' ? 'Races' : 'Koşu'}`;
  }

  // 1. Filtrelenmiş verideki en hızlı dereceyi bulma
  let fastestWinner = null;
  let minSeconds = Infinity;
  statsWinners.forEach(w => {
    const secs = parseTimeToSeconds(w.time);
    if (secs && secs < minSeconds) {
      minSeconds = secs;
      fastestWinner = w;
    }
  });

  const cards = document.querySelectorAll('.stat-card-premium');
  if (cards.length >= 4) {
    const isFiltered = currentSearchQuery || currentDecade !== 'all' || onlyLegends;

    // Kart 1: En Hızlı Derece
    const c1Val = cards[0].querySelector('.stat-card-value');
    const c1Desc = cards[0].querySelector('.stat-card-desc');
    const c1Label = cards[0].querySelector('.stat-card-label');
    c1Label.textContent = activeLang === 'en'
      ? (isFiltered ? 'Filtered Fastest Time' : 'Unbroken Time Record')
      : (isFiltered ? 'Filtre En Hızlı Derece' : 'Kırılmaz Derece Rekoru');
    if (fastestWinner) {
      c1Val.textContent = fastestWinner.time;
      c1Desc.textContent = `${fastestWinner.horseName} (${fastestWinner.year})`;
    } else {
      c1Val.textContent = '-';
      c1Desc.textContent = activeLang === 'en' ? 'No time record found' : 'Derece kaydı bulunamadı';
    }

    // Kart 2: En Başarılı Jokey
    const jockeyCounts = {};
    statsWinners.forEach(w => {
      if (w.jockey) {
        jockeyCounts[w.jockey] = (jockeyCounts[w.jockey] || 0) + 1;
      }
    });
    let topJockey = '-';
    let maxJockeyWins = 0;
    Object.keys(jockeyCounts).forEach(j => {
      if (jockeyCounts[j] > maxJockeyWins) {
        maxJockeyWins = jockeyCounts[j];
        topJockey = j;
      }
    });

    const c2Val = cards[1].querySelector('.stat-card-value');
    const c2Desc = cards[1].querySelector('.stat-card-desc');
    const c2Label = cards[1].querySelector('.stat-card-label');
    c2Label.textContent = activeLang === 'en'
      ? (isFiltered ? 'Filtered Most Successful Jockey' : 'Most Successful Jockey')
      : (isFiltered ? 'Filtre En Başarılı Jokey' : 'En Başarılı Jokey');
    if (maxJockeyWins > 0) {
      c2Val.textContent = `${maxJockeyWins} ${activeLang === 'en' ? 'Wins' : 'Zafer'}`;
      c2Desc.textContent = topJockey;
    } else {
      c2Val.textContent = '-';
      c2Desc.textContent = activeLang === 'en' ? 'No jockey record found' : 'Jokey kaydı bulunamadı';
    }

    // Kart 3: En Başarılı Eküri / Sahip
    const ownerCounts = {};
    statsWinners.forEach(w => {
      if (w.owner) {
        const normOwner = normalizeOwnerName(w.owner);
        ownerCounts[normOwner] = (ownerCounts[normOwner] || 0) + 1;
      }
    });
    let topOwner = '-';
    let maxOwnerWins = 0;
    Object.keys(ownerCounts).forEach(o => {
      if (ownerCounts[o] > maxOwnerWins) {
        maxOwnerWins = ownerCounts[o];
        topOwner = o;
      }
    });

    const c3Val = cards[2].querySelector('.stat-card-value');
    const c3Desc = cards[2].querySelector('.stat-card-desc');
    const c3Label = cards[2].querySelector('.stat-card-label');
    c3Label.textContent = activeLang === 'en'
      ? (isFiltered ? 'Filtered Most Successful Stable' : 'Most Successful Stable')
      : (isFiltered ? 'Filtre En Başarılı Eküri' : 'En Başarılı Eküri');
    if (maxOwnerWins > 0) {
      c3Val.textContent = `${maxOwnerWins} ${activeLang === 'en' ? 'Wins' : 'Zafer'}`;
      c3Desc.textContent = topOwner.replace('Ekürisi', activeLang === 'en' ? 'Stable' : 'Ekürisi');
    } else {
      c3Val.textContent = '-';
      c3Desc.textContent = activeLang === 'en' ? 'No owner record found' : 'Sahip kaydı bulunamadı';
    }
  }
}

// HTML/CSS Yatay Bar Grafikleri Üreticisi
function renderBarCharts() {
  if (!jockeyBarChart || !ownerBarChart) return;

  // Exclude 2026 (or poll years) from statistics/analysis
  const statsWinners = filteredWinners.filter(w => w.year !== 2026 || isGazi2026Run());

  // 1. Jokey Galibiyet Grafiği
  const jockeyCounts = {};
  statsWinners.forEach(w => {
    if (w.jockey) {
      jockeyCounts[w.jockey] = (jockeyCounts[w.jockey] || 0) + 1;
    }
  });

  const sortedJockeys = Object.entries(jockeyCounts)
    .sort((a, b) => b[1] - a[1])
    .slice(0, 8);

  const maxJockeyWins = sortedJockeys.length > 0 ? sortedJockeys[0][1] : 1;

  jockeyBarChart.innerHTML = sortedJockeys.map(([jockey, count]) => {
    const avatarFile = getJockeyFilename(jockey);
    const percentage = (count / maxJockeyWins) * 100;
    return `
      <div class="bar-chart-item clickable-jockey" data-jockey="${jockey}">
        <div class="bar-chart-label-container" title="${jockey}">
          <img class="bar-chart-avatar" loading="lazy" src="${getJockeyImageUrl(avatarFile)}" alt="${jockey}" onerror="this.src='${getJockeyImageUrl('default_jockey.webp')}'">
          <span class="bar-chart-label">${jockey}</span>
        </div>
        <div class="bar-chart-wrapper">
          <div class="bar-chart-fill" style="width: ${percentage}%"></div>
        </div>
        <span class="bar-chart-value">${count} ${activeLang === 'en' ? 'Wins' : 'Zafer'}</span>
      </div>
    `;
  }).join('');

  // 2. Sahip / Eküri Galibiyet Grafiği
  const ownerCounts = {};
  statsWinners.forEach(w => {
    if (w.owner) {
      const norm = normalizeOwnerName(w.owner);
      ownerCounts[norm] = (ownerCounts[norm] || 0) + 1;
    }
  });

  const sortedOwners = Object.entries(ownerCounts)
    .sort((a, b) => b[1] - a[1])
    .slice(0, 8);

  const maxOwnerWins = sortedOwners.length > 0 ? sortedOwners[0][1] : 1;

  ownerBarChart.innerHTML = sortedOwners.map(([owner, count]) => {
    const percentage = (count / maxOwnerWins) * 100;
    const translatedOwner = owner.replace('Ekürisi', activeLang === 'en' ? 'Stable' : 'Ekürisi');
    return `
      <div class="bar-chart-item clickable-owner" data-owner="${owner}">
        <div class="bar-chart-label-container" title="${translatedOwner}">
          <span class="bar-chart-label">${translatedOwner}</span>
        </div>
        <div class="bar-chart-wrapper">
          <div class="bar-chart-fill gold-fill" style="width: ${percentage}%"></div>
        </div>
        <span class="bar-chart-value">${count} ${activeLang === 'en' ? 'Wins' : 'Zafer'}</span>
      </div>
    `;
  }).join('');
}

function drawTimeTrendChart() {
  if (!timeTrendCanvas) return;

  // Update Chart Title and Subtitle dynamically based on chartViewMode and activeLang
  const chartTitleEl = document.getElementById('chart-main-title');
  const chartSubtitleEl = document.getElementById('chart-main-subtitle');
  if (chartTitleEl && chartSubtitleEl) {
    const titleKey = `chart_title_${chartViewMode}`;
    const subtitleKey = `chart_subtitle_${chartViewMode}`;
    if (translations[activeLang]) {
      chartTitleEl.textContent = translations[activeLang][titleKey] || '';
      chartSubtitleEl.textContent = translations[activeLang][subtitleKey] || '';
    }
  }

  const rect = timeTrendCanvas.getBoundingClientRect();
  if (rect.width === 0 || rect.height === 0) return; // Görünür değilse çizim yapılmaz

  // Retina Ekran Piksel Netliği (High DPI)
  const dpr = window.devicePixelRatio || 1;
  timeTrendCanvas.width = rect.width * dpr;
  timeTrendCanvas.height = rect.height * dpr;

  const ctx = timeTrendCanvas.getContext('2d');
  ctx.clearRect(0, 0, timeTrendCanvas.width, timeTrendCanvas.height);
  ctx.scale(dpr, dpr);

  // Derece veya ikramiye kaydı olan şampiyonları yıllara göre artan sırala
  const chartData = [...filteredWinners]
    .filter(w => {
      if (chartViewMode === 'prize') {
        return w.year >= 1990 && w.year <= 2026 && prizeGoldData[w.year] !== undefined;
      }
      return (w.year !== 2026 || isGazi2026Run()) && parseTimeToSeconds(w.time) !== null;
    })
    .sort((a, b) => a.year - b.year);

  if (chartData.length < 2) {
    ctx.fillStyle = '#a7b2ad';
    ctx.font = '14px "Plus Jakarta Sans", sans-serif';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillText(activeLang === 'en' ? 'Insufficient data for chart rendering.' : 'Grafik çizimi için yetersiz veri var.', rect.width / 2, rect.height / 2);
    return;
  }

  // Yıllara göre veri noktalarını haritala
  const chartMappedData = chartData.map(w => {
    if (chartViewMode === 'prize') {
      const goldVal = prizeGoldData[w.year].gold;
      return { data: w, value: goldVal };
    }
    const secs = parseTimeToSeconds(w.time);
    const distance = w.year <= 1931 ? 2000 : 2400; // 1927-1931 was 2000m, rest is 2400m
    const speed = (distance / secs) * 3.6; // km/h
    const value = chartViewMode === 'time' ? secs : speed;
    return { data: w, value, secs, speed, distance };
  });

  const minYear = chartData[0].year;
  const maxYear = chartData[chartData.length - 1].year;
  
  const values = chartMappedData.map(d => d.value);
  const minValRaw = Math.min(...values);
  const maxValRaw = Math.max(...values);

  // Dikey eksende %5 marj bırak (çizgi alt/üst sınırlara yapışmasın)
  const valRange = maxValRaw - minValRaw;
  const minVal = minValRaw - (valRange * 0.05 || 1);
  const maxVal = maxValRaw + (valRange * 0.05 || 1);

  // Padding Tanımları
  const padLeft = 60;
  const padRight = 30;
  const padTop = 30;
  const padBottom = 40;

  const plotWidth = rect.width - padLeft - padRight;
  const plotHeight = rect.height - padTop - padBottom;

  // Grid Çizgileri Stili
  ctx.strokeStyle = 'rgba(255, 255, 255, 0.035)';
  ctx.lineWidth = 1;
  ctx.fillStyle = '#5e6b65';
  ctx.font = '10px "Outfit", sans-serif';
  ctx.textAlign = 'right';
  ctx.textBaseline = 'middle';

  // Dipnot görünürlüğünü güncelle
  const footnote = document.getElementById('chart-footnote');
  if (footnote) {
    footnote.style.display = chartViewMode === 'prize' ? 'none' : 'flex';
  }

  // 1. 1927-1931 Yılları Arasındaki 2000m Alanını Tarayarak Vurgula (Sadece derece/hız modlarında)
  if (chartViewMode !== 'prize') {
    const x1927 = padLeft + ((1927 - minYear) / (maxYear - minYear)) * plotWidth;
    const x1931 = padLeft + ((1931 - minYear) / (maxYear - minYear)) * plotWidth;
    
    ctx.fillStyle = 'rgba(212, 175, 55, 0.04)';
    ctx.fillRect(x1927, padTop, x1931 - x1927, plotHeight);
    
    ctx.strokeStyle = 'rgba(212, 175, 55, 0.15)';
    ctx.lineWidth = 1;
    ctx.setLineDash([3, 4]);
    ctx.beginPath();
    ctx.moveTo(x1931, padTop);
    ctx.lineTo(x1931, padTop + plotHeight);
    ctx.stroke();
    ctx.setLineDash([]); // Kesikli çizgiyi sıfırla
  
    ctx.fillStyle = 'rgba(212, 175, 55, 0.45)';
    ctx.font = '9px "Outfit", sans-serif';
    ctx.textAlign = 'left';
    ctx.fillText(activeLang === 'en' ? '2000m Era' : '2000m Dönemi', x1927 + 6, padTop + 14);
  }

  // Grid Çizgileri Stili Sıfırlama/Geri Yükleme
  ctx.strokeStyle = 'rgba(255, 255, 255, 0.035)';
  ctx.fillStyle = '#5e6b65';
  ctx.textAlign = 'right';
  ctx.font = '10px "Outfit", sans-serif';

  // 2. Yatay Grid Çizgileri & Y Dereceleri/Hızları/Altınları
  const yGridLines = 5;
  for (let i = 0; i < yGridLines; i++) {
    const val = minVal + (i / (yGridLines - 1)) * (maxVal - minVal);
    const y = padTop + plotHeight - ((val - minVal) / (maxVal - minVal)) * plotHeight;
    
    // Grid Çizgisi
    ctx.beginPath();
    ctx.moveTo(padLeft, y);
    ctx.lineTo(rect.width - padRight, y);
    ctx.stroke();

    // Etiket Metni (Moda Göre Değişken)
    let label;
    if (chartViewMode === 'prize') {
      label = `${val.toFixed(0)} ${activeLang === 'en' ? 'Coins' : 'Adet'}`;
    } else if (chartViewMode === 'time') {
      label = formatSecondsToTime(val);
    } else {
      label = `${val.toFixed(1)} ${activeLang === 'en' ? 'km/h' : 'km/s'}`;
    }
    ctx.fillText(label, padLeft - 10, y);
  }

  // 3. Dikey Grid Çizgileri & X Yılları (Decades)
  ctx.textAlign = 'center';
  ctx.textBaseline = 'top';
  
  const startDecade = Math.ceil(minYear / 10) * 10;
  const endDecade = Math.floor(maxYear / 10) * 10;

  let decadeStep = 10;
  const yearDiff = maxYear - minYear;
  if (yearDiff > 75) decadeStep = 20;
  else if (yearDiff < 20) decadeStep = 2;
  else if (yearDiff < 40) decadeStep = 5;

  ctx.strokeStyle = 'rgba(255, 255, 255, 0.035)';
  for (let dec = startDecade; dec <= maxYear; dec += decadeStep) {
    if (dec < minYear) continue;
    const x = padLeft + ((dec - minYear) / (maxYear - minYear)) * plotWidth;

    // Dikey Çizgi
    ctx.beginPath();
    ctx.moveTo(x, padTop);
    ctx.lineTo(x, padTop + plotHeight);
    ctx.stroke();

    // Yıl Etiketi
    ctx.fillStyle = '#5e6b65';
    ctx.fillText(dec.toString(), x, rect.height - padBottom + 10);
  }

  // Her veri noktasının piksel koordinatlarını hesapla ve kaydet
  chartPoints = chartMappedData.map(d => {
    const x = padLeft + ((d.data.year - minYear) / (maxYear - minYear)) * plotWidth;
    const y = padTop + plotHeight - ((d.value - minVal) / (maxVal - minVal)) * plotHeight;
    return { x, y, data: d.data };
  });

  // 4. Grafiğin Altındaki Gradyan Alanı Çiz
  ctx.beginPath();
  ctx.moveTo(chartPoints[0].x, padTop + plotHeight);
  chartPoints.forEach(p => {
    ctx.lineTo(p.x, p.y);
  });
  ctx.lineTo(chartPoints[chartPoints.length - 1].x, padTop + plotHeight);
  ctx.closePath();

  const areaGrad = ctx.createLinearGradient(0, padTop, 0, padTop + plotHeight);
  areaGrad.addColorStop(0, 'rgba(212, 175, 55, 0.08)');
  areaGrad.addColorStop(1, 'rgba(212, 175, 55, 0.0)');
  ctx.fillStyle = areaGrad;
  ctx.fill();

  // 5. Hover Kılavuz Çizgisi (Dashed vertical guide line)
  if (hoverPoint) {
    ctx.strokeStyle = 'rgba(255, 255, 255, 0.12)';
    ctx.lineWidth = 1;
    ctx.setLineDash([4, 4]);
    ctx.beginPath();
    ctx.moveTo(hoverPoint.x, padTop);
    ctx.lineTo(hoverPoint.x, padTop + plotHeight);
    ctx.stroke();
    ctx.setLineDash([]); // Reset dashed state
  }

  // 6. Ana Çizgiyi (Trend Curve) Çiz
  ctx.beginPath();
  ctx.moveTo(chartPoints[0].x, chartPoints[0].y);
  for (let i = 1; i < chartPoints.length; i++) {
    ctx.lineTo(chartPoints[i].x, chartPoints[i].y);
  }
  ctx.strokeStyle = '#d4af37';
  ctx.lineWidth = 2.5;
  ctx.shadowColor = 'rgba(212, 175, 55, 0.25)';
  ctx.shadowBlur = 8;
  ctx.stroke();
  ctx.shadowBlur = 0; // Reset shadow glow

  // 7. Noktaları Çiz
  chartPoints.forEach(p => {
    const isLg = p.data.isLegend;
    ctx.fillStyle = isLg ? (p.data.accentColor || '#d4af37') : '#030705';
    ctx.strokeStyle = isLg ? '#fff' : '#d4af37';
    ctx.lineWidth = isLg ? 2 : 1.5;

    ctx.beginPath();
    ctx.arc(p.x, p.y, isLg ? 5.5 : 3.5, 0, 2 * Math.PI);
    ctx.fill();
    ctx.stroke();
  });

  // 8. Hover Halkası Çiz
  if (hoverPoint) {
    const isLg = hoverPoint.data.isLegend;
    ctx.strokeStyle = '#fff';
    ctx.lineWidth = 2.5;
    ctx.fillStyle = isLg ? (hoverPoint.data.accentColor || '#d4af37') : '#d4af37';
    
    ctx.beginPath();
    ctx.arc(hoverPoint.x, hoverPoint.y, isLg ? 8 : 6.5, 0, 2 * Math.PI);
    ctx.fill();
    ctx.stroke();
  }
}

// Grafik Hover Hareketi Takipçisi
function handleChartMouseMove(e) {
  if (chartPoints.length === 0) return;

  const rect = timeTrendCanvas.getBoundingClientRect();
  const mouseX = e.clientX - rect.left;
  const mouseY = e.clientY - rect.top;

  // X ekseninde en yakın noktayı bul
  let closest = null;
  let minDist = Infinity;

  chartPoints.forEach(p => {
    const dist = Math.abs(p.x - mouseX);
    if (dist < minDist) {
      minDist = dist;
      closest = p;
    }
  });

  // Hover aralığı (25 piksel)
  const hoverRange = 25;
  if (closest && minDist < hoverRange) {
    if (hoverPoint !== closest) {
      hoverPoint = closest;
      
      // Kılavuz çizgisi ve noktayı çizmek için grafiği yenile
      drawTimeTrendChart();

      // Tooltip HTML'ini güncelle
      const w = closest.data;
      const translatedLegendTitle = w.isLegend ? (activeLang === 'en' ? (LEGEND_TITLES_EN[w.legendTitle] || w.legendTitle) : w.legendTitle) : '';

      if (chartTooltip) {
        if (chartViewMode === 'prize') {
          const goldInfo = prizeGoldData[w.year];
          const goldCoinsCount = goldInfo ? goldInfo.gold : 0;
          const nominalPrize = goldInfo ? goldInfo.prize : 0;
          const formattedNominal = `${nominalPrize.toLocaleString('tr-TR')} TL`;
          
          const goldText = activeLang === 'en'
            ? `${goldCoinsCount.toLocaleString('tr-TR')} Republican Gold Coins`
            : `${goldCoinsCount.toLocaleString('tr-TR')} Adet Cumhuriyet Altını`;

          chartTooltip.innerHTML = `
            <div class="tooltip-year">
              <span>${w.year}</span>
              ${w.isLegend ? `<span class="tooltip-legend-tag" style="background:${w.accentColor || '#d4af37'}">${translatedLegendTitle}</span>` : ''}
            </div>
            <div class="tooltip-row">
              <span class="tooltip-label">${activeLang === 'en' ? 'Champion:' : 'Şampiyon:'}</span>
              <span class="tooltip-val">${w.horseName}</span>
            </div>
            <div class="tooltip-row">
              <span class="tooltip-label">${activeLang === 'en' ? 'Jockey:' : 'Jokey:'}</span>
              <span class="tooltip-val">${w.jockey}</span>
            </div>
            <div class="tooltip-row">
              <span class="tooltip-label">${activeLang === 'en' ? 'Nominal Prize:' : 'Nominal Ödül:'}</span>
              <span class="tooltip-val">${formattedNominal}</span>
            </div>
            <div class="tooltip-row">
              <span class="tooltip-label">${activeLang === 'en' ? 'Gold Equivalent:' : 'Altın Karşılığı:'}</span>
              <span class="tooltip-val highlight-gold">${goldText}</span>
            </div>
          `;
        } else {
          const distance = w.year <= 1931 ? 2000 : 2400;
          const secs = parseTimeToSeconds(w.time);
          const speedKmh = secs ? (distance / secs * 3.6).toFixed(2) : null;
          const timeText = (activeLang === 'en' && w.time === 'Kayıt Yok') ? 'No Record' : w.time;
          
          chartTooltip.innerHTML = `
            <div class="tooltip-year">
              <span>${w.year}</span>
              ${w.isLegend ? `<span class="tooltip-legend-tag" style="background:${w.accentColor || '#d4af37'}">${translatedLegendTitle}</span>` : ''}
            </div>
            <div class="tooltip-row">
              <span class="tooltip-label">${activeLang === 'en' ? 'Champion:' : 'Şampiyon:'}</span>
              <span class="tooltip-val">${w.horseName}</span>
            </div>
            <div class="tooltip-row">
              <span class="tooltip-label">${activeLang === 'en' ? 'Jockey:' : 'Jokey:'}</span>
              <span class="tooltip-val">${w.jockey}</span>
            </div>
            <div class="tooltip-row">
              <span class="tooltip-label">${activeLang === 'en' ? 'Distance:' : 'Mesafe:'}</span>
              <span class="tooltip-val">${distance} m</span>
            </div>
            <div class="tooltip-row">
              <span class="tooltip-label">${activeLang === 'en' ? 'Time:' : 'Derece:'}</span>
              <span class="tooltip-val ${chartViewMode === 'time' ? 'highlight-gold' : ''}">${timeText}</span>
            </div>
            ${speedKmh ? `
            <div class="tooltip-row">
              <span class="tooltip-label">${activeLang === 'en' ? 'Avg Speed:' : 'Ort. Hız:'}</span>
              <span class="tooltip-val ${chartViewMode === 'speed' ? 'highlight-gold' : ''}">${speedKmh} ${activeLang === 'en' ? 'km/h' : 'km/s'}</span>
            </div>
            ` : ''}
          `;
        }
        chartTooltip.style.display = 'block';

        // Tooltip'in ekran sınırlarından taşmasını önle
        let leftPos = closest.x + 15;
        if (leftPos + 200 > rect.width) {
          leftPos = closest.x - 205;
        }
        let topPos = closest.y - 45;
        if (topPos < 10) topPos = 10;
        if (topPos + 140 > rect.height) topPos = rect.height - 150;

        chartTooltip.style.left = `${leftPos}px`;
        chartTooltip.style.top = `${topPos}px`;
        chartTooltip.style.opacity = '1';
      }
    }
  } else {
    handleChartMouseLeave();
  }
}

// Grafik Hover Ayrılma Takipçisi
function handleChartMouseLeave() {
  if (hoverPoint !== null) {
    hoverPoint = null;
    drawTimeTrendChart();
    if (chartTooltip) {
      chartTooltip.style.opacity = '0';
      chartTooltip.style.display = 'none';
    }
  }
}



// Benzer eküri ve sahipleri gruplandırır
function normalizeOwnerName(ownerName) {
  if (!ownerName) return 'Bilinmiyor';
  if (ownerName.includes('Eliyeşil')) return 'Eliyeşil Ekürisi';
  if (ownerName.includes('Giraud')) return 'William Giraud';
  if (ownerName.includes('Atman')) return 'Atman Ekürisi';
  if (ownerName.includes('Kura')) return 'Cemal Kura Ekürisi';
  if (ownerName.includes('Temel')) return 'Salih Temel Ekürisi';
  if (ownerName.includes('Kitapçı')) return 'Muammer Kitapçı';
  if (ownerName.includes('Kurt')) return 'Mehmet Kurt';
  if (ownerName.includes('Özcan')) return 'Sedat Özcan';
  if (ownerName.includes('Yüzatlı')) return 'Fikret Yüzatlı';
  return ownerName;
}

// Yıl butonlarının ortadakinin en büyük, kenardakilerin en küçük olacağı şekilde ölçeklenmesi
function updateYearButtonScales() {
  if (!yearSliderTrack) return;

  const buttons = yearSliderTrack.querySelectorAll('.btn-year');

  // Sadece mobil ve tablet çözünürlüklerinde (<= 992px) odak efekti uygula, desktopta temizle
  if (window.innerWidth > 992) {
    buttons.forEach(btn => {
      btn.style.transform = '';
      btn.style.opacity = '';
    });
    return;
  }

  const trackRect = yearSliderTrack.getBoundingClientRect();
  const trackCenter = trackRect.left + trackRect.width / 2;

  buttons.forEach(btn => {
    const btnRect = btn.getBoundingClientRect();
    const btnCenter = btnRect.left + btnRect.width / 2;
    const distance = Math.abs(btnCenter - trackCenter);
    
    // Yıl rayının yarısını maksimum mesafe kabul et
    const maxDist = trackRect.width / 2 || 1;
    const ratio = Math.min(distance / maxDist, 1); // 0 (ortada) ile 1 (en kenarda) arası
    
    // Ölçek: Ortadaki 1.25, kenardakiler 0.8
    const scale = 1.25 - ratio * 0.45;
    // Opaklık: Ortadaki 1.0, kenardakiler 0.35
    const opacity = 1.0 - ratio * 0.65;
    
    btn.style.transform = `scale(${scale})`;
    btn.style.opacity = opacity;
  });
}

// Gazi Koşusu Geri Sayım Kronometresi
function initGaziCountdown() {
  const targetDate = new Date('2026-06-28T17:15:00+03:00').getTime();

  const closeBtn = document.getElementById('close-countdown-btn');
  const countdownOverlay = document.getElementById('gazi-countdown-overlay');

  if (countdownOverlay && localStorage.getItem('gazi_countdown_closed') === 'true') {
    countdownOverlay.style.display = 'none';
    return;
  }

  if (closeBtn && countdownOverlay) {
    closeBtn.addEventListener('click', () => {
      countdownOverlay.style.display = 'none';
      document.body.classList.remove('countdown-visible');
      localStorage.setItem('gazi_countdown_closed', 'true');
    });
  }

  let intervalId = null;

  function updateCountdown() {
    const now = new Date().getTime();
    const diff = targetDate - now;

    const daysEl = document.getElementById('countdown-days');
    const hoursEl = document.getElementById('countdown-hours');
    const minsEl = document.getElementById('countdown-minutes');
    const secsEl = document.getElementById('countdown-seconds');

    if (diff <= 0) {
      if (countdownOverlay) {
        countdownOverlay.style.display = 'none';
      }
      if (intervalId) {
        clearInterval(intervalId);
      }
      return;
    }

    const days = Math.floor(diff / (1000 * 60 * 60 * 24));
    const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
    const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
    const seconds = Math.floor((diff % (1000 * 60)) / 1000);

    if (daysEl) daysEl.textContent = String(days).padStart(2, '0');
    if (hoursEl) hoursEl.textContent = String(hours).padStart(2, '0');
    if (minsEl) minsEl.textContent = String(minutes).padStart(2, '0');
    if (secsEl) secsEl.textContent = String(seconds).padStart(2, '0');
  }

  updateCountdown();
  const initialDiff = targetDate - new Date().getTime();
  if (initialDiff > 0) {
    document.body.classList.add('countdown-visible');
    intervalId = setInterval(updateCountdown, 1000);
  }
}



function copyShareText(text) {
  // Fallback to clipboard
  navigator.clipboard.writeText(text).then(() => {
    const toastMsg = activeLang === 'en'
      ? "Your score has been copied to the clipboard! You are ready to share."
      : "Skorunuz panoya kopyalandı! Paylaşmaya hazırsınız.";
    showToast(toastMsg);
  }).catch(() => {
    const alertMsg = activeLang === 'en'
      ? "Failed to copy result: "
      : "Sonuç kopyalanamadı: ";
    alert(alertMsg + text);
  });
}

// --- ARAMA ÇUBUĞU OTOMATİK TAMAMLAMA (AUTOCOMPLETE) MANTIĞI ---

let suggestionDatabase = [];
let highlightedSuggestionIndex = -1;

function initSearchAutocomplete() {
  const searchActionBtn = document.getElementById('search-action-btn');
  const suggestionsBox = document.getElementById('search-suggestions');
  if (!searchInput || !searchActionBtn || !suggestionsBox) return;

  // 1. Build suggestion database from gaziWinners
  const horses = new Set();
  const jockeys = new Set();
  const owners = new Set();
  const years = new Set();

  gaziWinners.forEach(winner => {
    if (winner.horseName && winner.horseName !== "Henüz Koşulmadı") {
      horses.add(winner.horseName);
    }
    if (winner.jockey && winner.jockey !== "Anket") {
      jockeys.add(winner.jockey);
    }
    if (winner.owner && winner.owner !== "Anket") {
      owners.add(winner.owner);
    }
    if (winner.year) {
      years.add(String(winner.year));
    }
  });

  // Convert to database items: { value, type, icon, badge }
  horses.forEach(name => {
    suggestionDatabase.push({ value: name, type: 'at', icon: '🐴', badge: 'At' });
  });
  jockeys.forEach(name => {
    suggestionDatabase.push({ value: name, type: 'jokey', icon: '🏇', badge: 'Jokey' });
  });
  owners.forEach(name => {
    suggestionDatabase.push({ value: name, type: 'sahip', icon: '👤', badge: 'Sahip' });
  });
  years.forEach(yr => {
    suggestionDatabase.push({ value: yr, type: 'yıl', icon: '📅', badge: 'Yıl' });
  });

  // 2. Setup Events
  
  // Clear search on action button click
  searchActionBtn.addEventListener('click', () => {
    if (searchInput.value !== '') {
      searchInput.value = '';
      currentSearchQuery = '';
      searchActionBtn.textContent = '🔍';
      searchActionBtn.title = 'Ara';
      hideSuggestions();
      applyFilters();
      searchInput.focus();
    }
  });

  // Input event: update button, render suggestions
  searchInput.addEventListener('input', (e) => {
    const val = e.target.value.trim();
    if (val !== '') {
      searchActionBtn.textContent = '✕';
      searchActionBtn.title = 'Temizle';
    } else {
      searchActionBtn.textContent = '🔍';
      searchActionBtn.title = 'Ara';
    }

    currentSearchQuery = val.toLowerCase();
    applyFilters();

    if (val.length >= 2) {
      showSuggestions(val);
    } else {
      hideSuggestions();
    }
  });

  // Keyboard navigation on input
  searchInput.addEventListener('keydown', (e) => {
    if (suggestionsBox.style.display === 'none') return;

    const items = suggestionsBox.querySelectorAll('.suggestion-item');
    if (items.length === 0) return;

    if (e.key === 'ArrowDown') {
      e.preventDefault();
      highlightedSuggestionIndex = (highlightedSuggestionIndex + 1) % items.length;
      updateSuggestionHighlight(items);
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      highlightedSuggestionIndex = (highlightedSuggestionIndex - 1 + items.length) % items.length;
      updateSuggestionHighlight(items);
    } else if (e.key === 'Enter') {
      e.preventDefault();
      if (highlightedSuggestionIndex >= 0 && highlightedSuggestionIndex < items.length) {
        items[highlightedSuggestionIndex].click();
      }
    } else if (e.key === 'Escape') {
      e.preventDefault();
      hideSuggestions();
    }
  });

  // Hide suggestions when clicking outside
  document.addEventListener('click', (e) => {
    if (e.target !== searchInput && e.target !== suggestionsBox && !suggestionsBox.contains(e.target)) {
      hideSuggestions();
    }
  });
}

function showSuggestions(query) {
  const suggestionsBox = document.getElementById('search-suggestions');
  if (!suggestionsBox) return;

  const cleanQuery = query.toLowerCase();
  
  // Filter and prioritize matches
  const matches = suggestionDatabase.filter(item => {
    return item.value.toLowerCase().includes(cleanQuery);
  });

  // Sort: prioritize matches starting with the query
  matches.sort((a, b) => {
    const aStartsWith = a.value.toLowerCase().startsWith(cleanQuery);
    const bStartsWith = b.value.toLowerCase().startsWith(cleanQuery);
    if (aStartsWith && !bStartsWith) return -1;
    if (!aStartsWith && bStartsWith) return 1;
    return a.value.localeCompare(b.value);
  });

  // Take top 6
  const displayMatches = matches.slice(0, 6);

  if (displayMatches.length === 0) {
    hideSuggestions();
    return;
  }

  suggestionsBox.innerHTML = '';
  highlightedSuggestionIndex = -1;

  displayMatches.forEach((item, idx) => {
    const div = document.createElement('div');
    div.className = 'suggestion-item';
    div.setAttribute('data-value', item.value);
    
    // Highlight matched text
    const index = item.value.toLowerCase().indexOf(cleanQuery);
    let textHtml = item.value;
    if (index >= 0) {
      const matchPart = item.value.substring(index, index + query.length);
      textHtml = item.value.replace(new RegExp(matchPart, 'i'), `<strong>${matchPart}</strong>`);
    }

    div.innerHTML = `
      <span class="suggestion-icon">${item.icon}</span>
      <span class="suggestion-text">${textHtml}</span>
      <span class="suggestion-badge">${item.badge}</span>
    `;

    div.addEventListener('click', () => {
      searchInput.value = item.value;
      currentSearchQuery = item.value.toLowerCase();
      
      const searchActionBtn = document.getElementById('search-action-btn');
      if (searchActionBtn) {
        searchActionBtn.textContent = '✕';
        searchActionBtn.title = 'Temizle';
      }

      applyFilters();
      hideSuggestions();
    });

    suggestionsBox.appendChild(div);
  });

  suggestionsBox.style.display = 'flex';
}

function hideSuggestions() {
  const suggestionsBox = document.getElementById('search-suggestions');
  if (suggestionsBox) {
    suggestionsBox.style.display = 'none';
    suggestionsBox.innerHTML = '';
  }
  highlightedSuggestionIndex = -1;
}

function updateSuggestionHighlight(items) {
  items.forEach((item, idx) => {
    if (idx === highlightedSuggestionIndex) {
      item.classList.add('highlighted');
      item.scrollIntoView({ block: 'nearest' });
    } else {
      item.classList.remove('highlighted');
    }
  });
}

// Logo Tıklaması ile Sayfayı Başlangıç Konumuna Getirme Mantığı
function resetToInitialState() {
  hashInitialized = false;
  if (window.location.hash) {
    history.replaceState(null, null, window.location.pathname + window.location.search);
  }
  // 1. Durum Değişkenlerini Sıfırla
  currentSearchQuery = '';
  currentDecade = 'all';
  onlyLegends = false;
  activeYear = isGazi2026Run() ? 2026 : 2025;

  // 2. Arama ve Filtre Arayüzlerini Sıfırla
  if (searchInput) {
    searchInput.value = '';
  }
  
  const searchActionBtn = document.getElementById('search-action-btn');
  if (searchActionBtn) {
    searchActionBtn.textContent = '🔍';
    searchActionBtn.title = 'Ara';
  }
  
  hideSuggestions();

  if (legendToggle) {
    legendToggle.checked = false;
  }

  // Dönem butonlarını sıfırla
  if (decadeFilterContainer) {
    decadeFilterContainer.querySelectorAll('.btn-decade').forEach(btn => {
      btn.classList.remove('active');
      if (btn.getAttribute('data-decade') === 'all') {
        btn.classList.add('active');
      }
    });
  }

  // Şehir butonlarını sıfırla
  currentCity = 'all';
  if (cityFilterContainer) {
    cityFilterContainer.querySelectorAll('.btn-decade').forEach(btn => {
      btn.classList.remove('active');
      if (btn.getAttribute('data-city') === 'all') {
        btn.classList.add('active');
      }
    });
  }

  // 3. Tab Değişikliği (Eğer İstatistik Sayfasındaysa Zaman Tüneline Dön)
  switchTab('timeline');

  // 4. Oynayan Video Varsa Durdur ve Gizle
  stopAndHideVideo();

  // 5. Filtreleri Yeniden Uygula
  applyFilters();

  // 6. Zaman Tüneli Rayını defaultYear'a Ortala ve Kartı Güncelle
  const defaultYear = isGazi2026Run() ? 2026 : 2025;
  const winner = gaziWinners.find(w => w.year === defaultYear);
  updateChampionCard(winner);

  if (yearSliderTrack) {
    yearSliderTrack.querySelectorAll('.btn-year').forEach(btn => {
      btn.classList.remove('active');
      if (parseInt(btn.getAttribute('data-year')) === defaultYear) {
        btn.classList.add('active');
      }
    });
  }

  centerActiveYearButton();
  updateYearButtonScales();
}



// Contextual Swipe Hint Overlay when user clicks navigation arrows on mobile
function showSwipeHint() {
  const overlay = document.getElementById('swipe-hint-overlay');
  if (!overlay) return;

  const isMobile = window.innerWidth <= 768;
  const isShown = localStorage.getItem('swipe_hint_shown') === 'true';

  if (isMobile && !isShown) {
    // Show the overlay block
    overlay.style.display = 'flex';
    
    // Clear any existing timeouts to prevent animation collisions
    if (window.swipeHintTimeout) clearTimeout(window.swipeHintTimeout);
    if (window.swipeHintFadeTimeout) clearTimeout(window.swipeHintFadeTimeout);

    // Trigger fade in animation slightly after
    setTimeout(() => {
      overlay.classList.add('visible');
    }, 100);

    // Dismiss listener function
    const dismissOverlay = () => {
      if (overlay.classList.contains('visible')) {
        overlay.classList.remove('visible');
        localStorage.setItem('swipe_hint_shown', 'true');
        
        // Fully hide after fade animation completes
        window.swipeHintFadeTimeout = setTimeout(() => {
          overlay.style.display = 'none';
        }, 500);
      }

      // Clean up event listeners
      document.removeEventListener('touchstart', dismissOverlay);
      document.removeEventListener('mousedown', dismissOverlay);
      arenaPrevBtn.removeEventListener('click', dismissOverlay);
      arenaNextBtn.removeEventListener('click', dismissOverlay);
    };

    // Listen for any touch or click event to dismiss the overlay (with small delay to prevent instant dismiss on the triggering click)
    setTimeout(() => {
      document.addEventListener('touchstart', dismissOverlay, { once: true, passive: true });
      document.addEventListener('mousedown', dismissOverlay, { once: true });
      arenaPrevBtn.addEventListener('click', dismissOverlay, { once: true });
      arenaNextBtn.addEventListener('click', dismissOverlay, { once: true });
    }, 100);

    // Auto-dismiss after 2.5 seconds
    window.swipeHintTimeout = setTimeout(() => {
      dismissOverlay();
    }, 2500);
  }
}

// ==========================================================================
// Language Switching & Translation Functions
// ==========================================================================

function initLanguageSwitcher() {
  const btnTr = document.getElementById('btn-lang-tr');
  const btnEn = document.getElementById('btn-lang-en');
  if (btnTr) {
    btnTr.addEventListener('click', () => {
      if (activeLang !== 'tr') switchLanguage('tr');
    });
  }
  if (btnEn) {
    btnEn.addEventListener('click', () => {
      if (activeLang !== 'en') switchLanguage('en');
    });
  }
}

function switchLanguage(lang) {
  activeLang = lang;
  localStorage.setItem('gazi_lang', lang);
  document.documentElement.setAttribute('lang', lang);

  const btnTr = document.getElementById('btn-lang-tr');
  const btnEn = document.getElementById('btn-lang-en');
  if (btnTr && btnEn) {
    if (lang === 'en') {
      btnTr.classList.remove('active');
      btnEn.classList.add('active');
    } else {
      btnTr.classList.add('active');
      btnEn.classList.remove('active');
    }
  }

  // Translate static data-i18n elements
  const elements = document.querySelectorAll('[data-i18n]');
  elements.forEach(el => {
    const key = el.getAttribute('data-i18n');
    if (translations[lang] && translations[lang][key]) {
      const translationText = translations[lang][key];
      if (translationText.includes('<') && translationText.includes('>')) {
        el.innerHTML = translationText;
      } else {
        el.textContent = translationText;
      }
    }
  });

  // Translate dynamic stats subtitle
  updateStatsSubtitle();

  // Translate placeholder attributes
  const placeholderElements = document.querySelectorAll('[data-i18n-placeholder]');
  placeholderElements.forEach(el => {
    const key = el.getAttribute('data-i18n-placeholder');
    if (translations[lang] && translations[lang][key]) {
      el.setAttribute('placeholder', translations[lang][key]);
    }
  });

  // Translate title attributes
  const titleElements = document.querySelectorAll('[data-i18n-title]');
  titleElements.forEach(el => {
    const key = el.getAttribute('data-i18n-title');
    if (translations[lang] && translations[lang][key]) {
      el.setAttribute('title', translations[lang][key]);
    }
  });

  // Re-render dynamic components
  const currentWinner = filteredWinners.find(w => w.year === activeYear);
  if (currentWinner) {
    updateChampionCard(currentWinner);
  }

  renderStatistics();
  
  if (activeTab === 'stats') {
    drawTimeTrendChart();
    renderBarCharts();
  }

  // Update Quiz screen texts dynamically if active
  if (window.quizInitialized) {
    import('./modules/quiz.js').then(({ updateActiveQuizScreenLang }) => {
      updateActiveQuizScreenLang(() => activeLang);
    });
  }

  // Update Simulator screen texts dynamically if active
  if (window.simulatorInitialized) {
    import('./modules/simulator.js').then(({ updateActiveSimulatorLang }) => {
      updateActiveSimulatorLang(() => activeLang);
    });
  }
}

function getTranslatedStory(winner) {
  if (winner.year === 2026) {
    return "The 100th Gazi Derby run on June 28, 2026 was won by Emrah Nalçakan's Bay Nalçakan, under the guidance of jockey Halis Karataş with a time of 2.29.49. Races are not won by chance. Bay Nalçakan put on his best performance on the exact right day. Halis Karataş once again proved why he is considered one of the greatest jockeys in history.";
  }
  if (winner.year === 1996) {
    return "His record time of 2.26.22 remains unbroken for 30 years. The love-filled legend who entered the starting gate as the crowd went silent, flew from last place to victory in the final stretch, and became one with Halis Karataş. Additionally, Bold Pilot achieved the Triple Crown title by winning the Erkek Tay Deneme, Gazi Derby, and Ankara Stakes in the same year.";
  }
  if (winner.year === 1927) {
    return "The winner of the first Gazi Derby, organized under the encouragement of Mustafa Kemal Atatürk. Held on June 10, 1927 in Ankara, this first race for 3-year-olds and up was won by Ali Muhiddin Hacıbekir's Neriman with jockey İhsan Atçı, watched by Atatürk and İsmet İnönü together.";
  }

  const englishAdditions = {
    1929: " In the early years of the Gazi Derby, there was a great interest from statesmen; Cap Gris Nez won the cup as the thoroughbred of Celal Bayar (under the name Galip Bey), a founder of the Turkish Republic and future president. Furthermore, Cap Gris Nez's offspring Yılmazkaya and the legendary Karayel are also Gazi champions.",
    1930: " The founder of the Turkish Republic, İsmet İnönü, experienced the pride of winning this race with his own horse Olgo.",
    1932: " Starting from 1932, the derby was restricted to 3-year-old domestic Thoroughbreds, and the race distance was increased from 2000 meters to 2400 meters.",
    1936: " With the completion of the Great Ankara Hippodrome, designed by Italian architect Paolo Vietti Violi upon Atatürk's orders, the Gazi Derby moved to its new home from 1936 onwards.",
    1938: " Due to the passing of Atatürk, this historic race was run only in 1938 under the name 'Atatürk Cup'.",
    1944: " Davut Akdı and his son Süleyman Akdı are the only father-son jockey duo in derby history to win the Gazi Derby twice each.",
    1945: " Ahmet Atman, one of the pioneers of Turkish racing, achieved his third and final Gazi Derby victory with Fettan.",
    1950: " She (1950) and her offspring Atlıhan (1961) are both Gazi champions.",
    1952: " The victory of Virago owned by Özdemir Atman represents the deep-rooted history of Atman Stable, whose owner would later become TJK president and win the derby in 1996 with the legendary Bold Pilot in record time.",
    1953: " The victory of Kusun owned by Şadi Eliyeşil is one of the early achievements of Eliyeşil Stable, which would go on to win the derby 13 times in total, holding an unbreakable record.",
    1954: " His dam Fleche d'Or became the most successful mare in derby history, producing three Gazi champions: Beau Brummel (1954), La Fleche (1956), and Beau Manoir (1959).",
    1956: " In 1956, La Fleche and Cantatrice made history by sharing the first place. Additionally, her dam Fleche d'Or produced three Gazi winners: Beau Brummel (1954), La Fleche (1956), and Beau Manoir (1959).",
    1958: " William Giraud Stable achieved a dominant 3-year winning streak in 1958 (Icaros), 1959 (Beau Manoir), and 1960 (Helene de Troia), one of the most powerful streaks in derby history.",
    1959: " His dam Fleche d'Or became the most successful mare in derby history, producing three Gazi champions: Beau Brummel (1954), La Fleche (1956), and Beau Manoir (1959).",
    1960: " This victory marked the early steps of jockey Mümin Çılgın's legendary 9-win record in derby history.",
    1961: " His sire Cihangir made history by producing 6 Gazi champions (Atlıhan, Melikşah, Kayarlı, Pikehan, Minimo, Akkor). Furthermore, Atlıhan belongs to a legendary bloodline, with its dam Darling winning the derby in 1950.",
    1963: " Its sire Cihangir is a legendary stallion who produced 6 Gazi champions in total (Atlıhan, Melikşah, Kayarlı, Pikehan, Minimo, Akkor).",
    1964: " Its sire Cihangir is a legendary stallion who produced 6 Gazi champions in total (Atlıhan, Melikşah, Kayarlı, Pikehan, Minimo, Akkor).",
    1965: " Jockey Mümin Çılgın holds the record for the most Gazi Derby victories in history, winning it 9 times in total throughout his career.",
    1966: " Its sire Cihangir is a legendary stallion who produced 6 Gazi champions in total (Atlıhan, Melikşah, Kayarlı, Pikehan, Minimo, Akkor).",
    1967: " With this victory, jockey Kazım Yıldız won the Gazi Derby 7 times in total, writing his name in gold among the most successful jockeys in derby history.",
    1968: " In 1968, the Gazi Derby was run for the first time at Istanbul Veliefendi Hippodrome.",
    1969: " Sedat Okumuş won with Palet, achieving back-to-back Gazi Derby victories following his win with Asuvan the previous year, becoming the first jockey to do so at the beginning of the Veliefendi era.",
    1970: " From this year onwards, the winner was awarded the solid silver statue of Atatürk on horseback, sculpted by Şadi Çalık, first received by Sadettin's owner Sadun Atığ. Additionally, with this victory, Sadettin declared the first Triple Crown championship in Turkish racing history.",
    1971: " His sire Cihangir is the most successful stallion in derby history, with 6 of his offspring winning the Gazi Derby (Atlıhan, Melikşah, Kayarlı, Pikehan, Minimo, Akkor). Minimo also won the Ankara Stakes after this historic victory, making history as the only filly to win the Triple Crown.",
    1972: " His sire Cihangir is a legendary stallion with 6 Gazi-winning offspring (Atlıhan, Melikşah, Kayarlı, Pikehan, Minimo, Akkor). Akkor also made derby history together with its offspring Toraman.",
    1973: " The legendary thoroughbred, who was never defeated during his racing career, became a Triple Crown champion by also winning the Erkek Tay Deneme and Ankara Stakes in the same year.",
    1980: " Following the closure of the Ankara Hippodrome in 1979, the Gazi Derby moved permanently to Istanbul Veliefendi Hippodrome starting from 1980.",
    1983: " Its offspring Cartagena also won the Gazi Derby. Additionally, like his sire Karayel, Seren I won the Erkek Tay Deneme, Gazi Derby, and Ankara Stakes to achieve the Triple Crown title.",
    1985: " Ugurtay became a Triple Crown winner by also winning the Erkek Tay Deneme and Ankara Stakes in the same year.",
    1986: " In 1986, Eliyeşil Stable won the derby with Hafız and Kapkara taking the top two places for an entry-coupled victory. Hafiz also became a Triple Crown champion by winning the Erkek Tay Deneme and Ankara Stakes in the same year.",
    1990: " It shared the pride of winning the same trophy with its offspring Caprice.",
    1991: " With this victory, jockey Mümin Çılgın reached a total of 9 Gazi wins, holding the title of the jockey with the most Gazi Derby victories.",
    1993: " In 1993, Kurt Stable won the derby with The Best and I. Thunder Bold taking the top two places for an entry-coupled victory.",
    2000: " It shared the pride of winning the same trophy with its offspring Caprice. With a finish time of 2.26.71, Caprice is one of the champions who came closest to Bold Pilot's historic record (2.26.22).",
    2001: " Grand Ekinoks won the Erkek Tay Deneme and Ankara Stakes in the same year, becoming the first Triple Crown champion of the millennium and the last one in Turkish racing history.",
    2005: " With this victory, Eliyeşil Stable reached a total of 13 wins, becoming the most successful stable in the history of the Gazi Derby, with legendary thoroughbreds such as Karayel, Hafız, and Popular Demand.",
    2020: " Traditionally held in June, the race was postponed due to the COVID-19 pandemic and was held on August 30 (Victory Day).",
    2021: " Jockey Ahmet Çelik achieved an incredible, hard-to-break streak in derby history by winning 7 consecutive years between 2015 and 2021."
  };

  let translated = "";
  const regex = /([^’']*)’?(?:te|da|de|ta)? koşulan Gazi Koşusu’nu, (.*) orijinli (.*), (.*) ikramiyeyle kazandı\./;
  const match = winner.story.match(regex);
  if (match) {
    const datePart = match[1].trim();
    const pedigreePart = match[2].trim();
    const horseNamePart = match[3].trim();
    const prizePart = match[4].trim();

    let dateTranslated = datePart;
    const dateMatch = datePart.match(/(\d+)\s+([^\s]+)\s+(\d+)/);
    if (dateMatch) {
      const day = dateMatch[1];
      const monthTR = dateMatch[2];
      const year = dateMatch[3];
      const monthEN = MONTHS_TR_TO_EN[monthTR] || monthTR;
      dateTranslated = `${monthEN} ${day}, ${year}`;
    }

    const prizeTranslated = prizePart.replace(/\./g, ',');
    translated = `The Gazi Derby run on ${dateTranslated} was won by ${horseNamePart} (by ${pedigreePart}) with a prize pool of ${prizeTranslated}.`;
  } else {
    const defaultRegex = /(.*) idaresindeki (.*), (\d+) yılı Gazi Koşusu’nu kazanarak Türk yarışçılık tarihinin en prestijli kupasını kaldırmıştır\./;
    const defaultMatch = winner.story.match(defaultRegex);
    if (defaultMatch) {
      const jockey = defaultMatch[1].trim();
      const horse = defaultMatch[2].trim();
      const year = defaultMatch[3].trim();
      translated = `${horse}, ridden by ${jockey}, won the ${year} Gazi Derby, lifting the most prestigious trophy in Turkish racing history.`;
    } else {
      translated = winner.story;
    }
  }

  if (englishAdditions[winner.year]) {
    translated += englishAdditions[winner.year];
  }

  return translated;
}


